/**
 * B2B Cart Order Review
 * - Preserves Shopify cart updates and existing sync/auth flow.
 * - Adds grouped product metrics and labour/weight summaries.
 */
(function () {
  'use strict';

  /** @param {string} sel @param {ParentNode} [root] */
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  /** @param {string} value */
  function escCssIdent(value) {
    var s = String(value);
    if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(s);
    }
    return s.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  /** @param {(...args: unknown[]) => void} fn @param {number} wait */
  function debounced(fn, wait) {
    /** @type {number|undefined} */
    var t;
    /** @param {...unknown} args */
    function invoked() {
      var args = arguments;
      window.clearTimeout(t);
      t = window.setTimeout(function () {
        fn.apply(null, /** @type {unknown[]} */ (args));
      }, wait);
    }
    invoked.cancel = function () {
      window.clearTimeout(t);
    };
    return invoked;
  }

  /** @param {number} cents @param {string} code @param {boolean} withCode */
  function formatMoney(cents, code, withCode) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code || 'USD',
        currencyDisplay: withCode ? 'code' : 'narrowSymbol',
      }).format(amount);
    } catch {
      return (code || '') + ' ' + amount.toFixed(2);
    }
  }

  /** @param {'metric'|'cell'|'total'} [size] */
  function pricingSkeletonHtml(size) {
    var variant = size || 'cell';
    return (
      '<span class="b2b-cart-review__price-skeleton b2b-cart-review__price-skeleton--' +
      variant +
      '" aria-hidden="true"></span><span class="visually-hidden">Loading price</span>'
    );
  }

  /** @param {HTMLElement | null | undefined} el @param {'metric'|'cell'|'total'} [size] */
  function setPriceSkeleton(el, size) {
    if (!(el instanceof HTMLElement)) return;
    el.setAttribute('aria-busy', 'true');
    el.classList.remove('b2b-cart-review__metric-value--unavailable', 'b2b-cart-review__value--unavailable');
    el.innerHTML = pricingSkeletonHtml(size);
  }

  /** @param {HTMLElement | null | undefined} el @param {string} text */
  function setPriceText(el, text) {
    if (!(el instanceof HTMLElement)) return;
    el.removeAttribute('aria-busy');
    el.textContent = text;
  }

  /** @returns {Promise<{res: Response, data: Record<string, unknown>, txt: string}>} */
  async function fetchJson(url, opts) {
    var res = await fetch(url, {
      credentials: 'same-origin',
      ...(opts || {}),
      headers: {
        Accept: 'application/json',
        ...(opts && opts.headers),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data, txt };
  }

  /** @param {number} ms */
  function waitMs(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  /** @param {Record<string, unknown>} cfg */
  function readToken(cfg) {
    try {
      var v = window.localStorage.getItem(String(cfg.tokenStorageKey || ''));
      return v != null ? String(v).trim() : '';
    } catch {
      return '';
    }
  }

  /** @param {Record<string, unknown>} cfg */
  function readProfile(cfg) {
    try {
      var raw = window.localStorage.getItem(String(cfg.profileStorageKey || ''));
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o && typeof o === 'object' ? /** @type {Record<string, unknown>} */ (o) : null;
    } catch {
      return null;
    }
  }

  /** @param {Record<string, unknown>} cfg */
  function isTruthySetting(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  /**
   * Custom App cart-sync requires customers.id (positive integer), not ERP customerNumber.
   * @param {unknown} value
   * @returns {number|null}
   */
  function parsePositiveIntegerId(value) {
    var parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }

  /**
   * Prefer admin override email when enabled; otherwise use cfg.salesRepEmail
   * (already resolved in Liquid) or logged-in email fallback.
   * @param {Record<string, unknown>} cfg
   * @returns {string}
   */
  function getSalesRepEmail(cfg) {
    var overrideOn = isTruthySetting(cfg.salesRepEmailOverrideEnabled);
    var overrideEmail =
      cfg.salesRepEmailOverride != null ? String(cfg.salesRepEmailOverride).trim() : '';
    if (overrideOn && overrideEmail) return overrideEmail;

    var resolved = cfg.salesRepEmail != null ? String(cfg.salesRepEmail).trim() : '';
    if (resolved) return resolved;

    var loggedIn =
      cfg.loggedInSalesRepEmail != null ? String(cfg.loggedInSalesRepEmail).trim() : '';
    if (loggedIn) return loggedIn;

    var shop = cfg.shopCustomer;
    if (shop && typeof shop === 'object') {
      var shopEmail = /** @type {Record<string, unknown>} */ (shop).email;
      if (shopEmail != null) return String(shopEmail).trim();
    }
    return '';
  }

  /** @param {Record<string, unknown>} cfg */
  function isSalesRepOk(cfg) {
    if (!cfg.salesRepValidate) return true;
    if (getSalesRepEmail(cfg)) return true;
    if (cfg.customerLoggedInViaMetafieldOrCartAttr) return true;
    if (cfg.shopCustomer) return true;
    return false;
  }


  /** @param {string} path @param {string} fallback */
  function normalizeAppProxyPath(path, fallback) {
    var p = String(path || '').trim();
    if (!p) p = fallback;
    if (/^https?:\/\//i.test(p)) {
      try {
        var parsed = new URL(p);
        var pathname = parsed.pathname.replace(/\/+$/, '');
        return pathname || fallback;
      } catch (_) {
        return fallback;
      }
    }
    if (p.charAt(0) !== '/') p = '/' + p;
    return p.replace(/\/+$/, '');
  }

  /** @param {string} base @param {string} path */
  function joinUrl(base, path) {
    var b = String(base || '').replace(/\/+$/, '');
    var p = String(path || '');
    if (!p) return b;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.charAt(0) !== '/') p = '/' + p;
    return b ? b + p : p;
  }

  /** @param {string} label @param {Record<string, unknown>} payload */
  function logSyncOrderProxy(label, payload) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('[SyncOrder] ' + label, payload);
    }
  }

  /** @param {unknown} payload @returns {Record<string, unknown>} */
  function unwrapApiData(payload) {
    if (payload && typeof payload === 'object') {
      var root = /** @type {Record<string, unknown>} */ (payload);
      if (
        root.success === true &&
        root.data &&
        typeof root.data === 'object' &&
        !Array.isArray(root.data)
      ) {
        return /** @type {Record<string, unknown>} */ (root.data);
      }
      if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
        return /** @type {Record<string, unknown>} */ (root.data);
      }
      return root;
    }
    return {};
  }

  /** Backend cart-sync run statuses: QUEUED | PROCESSING | COMPLETED | FAILED */
  var ACTIVE_CART_SYNC_STATUSES = { QUEUED: true, PROCESSING: true, PENDING: true, RUNNING: true };
  var SUCCESS_CART_SYNC_STATUSES = { COMPLETED: true, SUCCESS: true };
  var FAILURE_CART_SYNC_STATUSES = { FAILED: true, CANCELLED: true, CONFLICT: true, EXPIRED: true };

  /** @type {AbortController|null} */
  var activeCartSyncPollAbort = null;

  function stopActiveCartSyncPoll() {
    if (!activeCartSyncPollAbort) return;
    try {
      activeCartSyncPollAbort.abort();
    } catch (_) {}
    activeCartSyncPollAbort = null;
  }

  /** @param {unknown} raw */
  function normalizeCartSyncStatus(raw) {
    return String(raw == null ? '' : raw)
      .trim()
      .toUpperCase();
  }

  /**
   * @param {Record<string, unknown>} responseData
   * @returns {string}
   */
  function readCartSyncStatus(responseData) {
    if (!responseData || typeof responseData !== 'object') return '';
    var row = /** @type {Record<string, unknown>} */ (responseData);
    var direct = normalizeCartSyncStatus(row.status != null ? row.status : row.syncStatus);
    if (direct) return direct;
    var nested = row.result && typeof row.result === 'object' ? /** @type {Record<string, unknown>} */ (row.result) : null;
    if (nested) {
      return normalizeCartSyncStatus(nested.status != null ? nested.status : nested.syncStatus);
    }
    return '';
  }

  /** @param {string} message @param {Record<string, unknown>} [extra] */
  function logCartSyncDebug(message, extra) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('[CartSync] ' + message, extra || {});
    }
  }

  /** @param {string} url @param {RequestInit} [opts] */
  async function fetchApiJson(url, opts) {
    var res = await fetch(url, {
      credentials: 'same-origin',
      ...(opts || {}),
      headers: {
        Accept: 'application/json',
        ...(opts && opts.headers ? opts.headers : {}),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data };
  }

  /** @param {string} raw */
  function splitCsv(raw) {
    return String(raw || '')
      .split(',')
      .map(function (p) {
        return p.trim();
      })
      .filter(Boolean);
  }

  /** @param {unknown} raw */
  function parseWeightNumber(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return null;
    var m = s.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!m) return null;
    var n = Number(m[0]);
    return Number.isFinite(n) ? n : null;
  }

  /** @param {number} value */
  function roundWeightValue(value) {
    var goldPricing = getGoldPricing();
    if (goldPricing && typeof goldPricing.round2 === 'function') {
      return goldPricing.round2(value);
    }
    var number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Number(Math.round(number + 'e2') + 'e-2');
  }

  /** @param {unknown} value */
  function roundMoney2(value) {
    var goldPricing = getGoldPricing();
    if (goldPricing && typeof goldPricing.round2 === 'function') {
      return goldPricing.round2(value);
    }
    var number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Number(Math.round(number + 'e2') + 'e-2');
  }

  /** @param {number} value */
  function formatWeightValue(value) {
    return roundWeightValue(value).toFixed(2) + ' g';
  }

  /** @param {unknown} raw @param {number[]} bucket */
  function collectWeightNumbers(raw, bucket) {
    if (raw == null) return;

    if (typeof raw === 'number') {
      if (Number.isFinite(raw) && raw > 0) bucket.push(raw);
      return;
    }

    if (Array.isArray(raw)) {
      raw.forEach(function (entry) {
        collectWeightNumbers(entry, bucket);
      });
      return;
    }

    if (typeof raw === 'object') {
      var obj = /** @type {Record<string, unknown>} */ (raw);
      var listKeys = [
        'weights',
        'selectedWeights',
        'selected_weights',
        'values',
        'entries',
        'items',
        'data',
      ];
      var foundList = false;
      for (var i = 0; i < listKeys.length; i++) {
        var lk = listKeys[i];
        if (obj[lk] == null) continue;
        foundList = true;
        collectWeightNumbers(obj[lk], bucket);
      }
      if (foundList) return;

      var scalarWeight =
        obj.weight != null
          ? obj.weight
          : obj.value != null
            ? obj.value
            : obj.grams != null
              ? obj.grams
              : null;
      var parsed = parseWeightNumber(scalarWeight);
      if (parsed == null || !Number.isFinite(parsed) || parsed <= 0) return;
      var qtyRaw =
        obj.quantity != null
          ? obj.quantity
          : obj.qty != null
            ? obj.qty
            : obj.count != null
              ? obj.count
              : 1;
      var qty = Math.max(1, Math.floor(Number(qtyRaw) || 1));
      for (var q = 0; q < qty; q++) bucket.push(parsed);
      return;
    }

    var s = String(raw).trim();
    if (!s) return;
    if ((s.charAt(0) === '[' && s.charAt(s.length - 1) === ']') || (s.charAt(0) === '{' && s.charAt(s.length - 1) === '}')) {
      try {
        collectWeightNumbers(JSON.parse(s), bucket);
        return;
      } catch {
        /* treat as CSV fallback */
      }
    }
    splitCsv(s).forEach(function (part) {
      var n = parseWeightNumber(part);
      if (n != null && Number.isFinite(n) && n > 0) bucket.push(n);
    });
  }

  /** @param {number[]} entries */
  function buildGroupedWeights(entries) {
    /** @type {Record<string, { weight: number, quantity: number }>} */
    var groupedByKey = {};
    var totalCount = 0;
    entries.forEach(function (n) {
      if (!Number.isFinite(n) || n <= 0) return;
      var normalized = roundWeightValue(n);
      if (!Number.isFinite(normalized) || normalized <= 0) return;
      var key = normalized.toFixed(2);
      if (!groupedByKey[key]) groupedByKey[key] = { weight: normalized, quantity: 0 };
      groupedByKey[key].quantity += 1;
      totalCount += 1;
    });

    var grouped = Object.keys(groupedByKey)
      .map(function (k) {
        return groupedByKey[k];
      })
      .sort(function (a, b) {
        return a.weight - b.weight;
      });

    return { grouped: grouped, totalCount: totalCount };
  }

  /** @param {unknown} raw */
  function normalizeSelectedWeights(raw) {
    /** @type {number[]} */
    var entries = [];
    collectWeightNumbers(raw, entries);
    return buildGroupedWeights(entries);
  }

  /** @param {Record<string, unknown>} item */
  function extractRawSelectedWeight(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    var keys = ['Weight', 'SelectedWeights', 'selected_weights', 'Weights', 'weight'];
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (props[key] != null && String(props[key]).trim()) return props[key];
    }
    return '';
  }

  var SELECTED_WEIGHT_ROWS_PER_TABLE = 15;

  /** @param {{ weight: number, quantity: number }[]} list @param {number} [rowsPerTable] */
  function splitIntoWeightColumns(list, rowsPerTable) {
    if (!list.length) return [];
    var size = Math.max(1, rowsPerTable || SELECTED_WEIGHT_ROWS_PER_TABLE);
    /** @type {{ weight: number, quantity: number }[][]} */
    var result = [];
    for (var i = 0; i < list.length; i += size) {
      result.push(list.slice(i, i + size));
    }
    return result;
  }

  /**
   * @param {{ weight: number, quantity: number }[]} column
   * @param {number} srStart
   */
  function renderSelectedWeightColumnHtml(column, srStart) {
    var sr = srStart;
    var rowsHtml = column
      .map(function (entry) {
        var rowHtml =
          '<div class="b2b-cart-review__selected-weight-value-row">' +
          '<span class="b2b-cart-review__selected-weight-sr">' +
          sr +
          '</span>' +
          '<span class="b2b-cart-review__selected-weight-grams">' +
          formatWeightValue(entry.weight) +
          '</span>' +
          '<span class="b2b-cart-review__selected-weight-qty">' +
          entry.quantity +
          '</span>' +
          '</div>';
        sr += 1;
        return rowHtml;
      })
      .join('');

    return (
      '<div class="b2b-cart-review__selected-weight-column">' +
      '<div class="b2b-cart-review__selected-weight-column-head">' +
      '<span class="b2b-cart-review__selected-weight-sr">SR. NO</span>' +
      '<span class="b2b-cart-review__selected-weight-grams">WEIGHT (EACH)</span>' +
      '<span class="b2b-cart-review__selected-weight-qty">QTY</span>' +
      '</div>' +
      '<div class="b2b-cart-review__selected-weight-column-body">' +
      rowsHtml +
      '</div>' +
      '</div>'
    );
  }

  /** @param {HTMLElement} row */
  function getWeightUiEls(row) {
    var trigger = qs('[data-selected-weight-toggle]', row);
    var triggerCount = qs('[data-selected-weight-trigger-count]', row);
    var panelRow = null;

    if (row.nextElementSibling instanceof HTMLElement && row.nextElementSibling.matches('[data-selected-weight-panel-row]')) {
      panelRow = row.nextElementSibling;
    } else {
      var lineKey = row.dataset.lineKey || row.dataset.cartLineKey || '';
      var parent = row.parentElement;
      if (lineKey && parent) {
        panelRow = parent.querySelector(
          '[data-selected-weight-panel-row][data-panel-for-line="' + escCssIdent(lineKey) + '"]'
        );
      }
    }

    var panel = null;
    if (trigger instanceof HTMLElement) {
      var panelId = trigger.getAttribute('aria-controls') || '';
      if (panelId) panel = document.getElementById(panelId);
    }
    if (!(panel instanceof HTMLElement) && panelRow instanceof HTMLElement) {
      panel = qs('[data-selected-weight-panel]', panelRow);
    }
    if (!(panel instanceof HTMLElement)) {
      panel = qs('[data-selected-weight-panel]', row);
    }

    return {
      trigger: trigger,
      triggerCount: triggerCount,
      panelRow: panelRow instanceof HTMLElement ? panelRow : null,
      panel: panel instanceof HTMLElement ? panel : null,
    };
  }

  function isMobileWeightLayout() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /** @param {HTMLElement} panel */
  function isWeightPanelHostedInItemRow(panel) {
    return !!(panel.closest && panel.closest('[data-cart-item]'));
  }

  /** @param {HTMLElement} root */
  function syncSelectedWeightPanelPlacement(root) {
    var mobile = isMobileWeightLayout();
    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var ui = getWeightUiEls(row);
      var panel = ui.panel;
      var panelRow = ui.panelRow;
      var cell = qs('.b2b-cart-review__selected-weights-cell', row);
      if (!(panel instanceof HTMLElement) || !(cell instanceof HTMLElement)) return;

      if (mobile) {
        if (panel.parentElement !== cell) cell.appendChild(panel);
        if (panelRow instanceof HTMLElement) {
          panelRow.hidden = true;
          panelRow.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      if (!(panelRow instanceof HTMLElement)) return;
      var hostTd = panelRow.querySelector('td');
      if (!(hostTd instanceof HTMLElement)) return;
      if (panel.parentElement !== hostTd) hostTd.appendChild(panel);
      var expanded = panel.dataset.expanded === 'true' && !panel.hidden;
      panelRow.hidden = !expanded;
      panelRow.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    });
  }

  /** @param {HTMLElement} panel */
  function stopPanelAnimation(panel) {
    var running = panel.__b2bPanelAnimation;
    if (running && typeof running.cancel === 'function') running.cancel();
    panel.__b2bPanelAnimation = null;
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} expanded
   * @param {{ restoreFocus?: boolean }} [opts]
   */
  function animatePanelState(panel, expanded, opts) {
    var panelRow = panel.closest('[data-selected-weight-panel-row]');
    var hostedInItem = isWeightPanelHostedInItemRow(panel);
    var card = panel.closest('[data-b2b-cart-review]');
    var triggerId = panel.getAttribute('aria-labelledby') || '';
    var trigger = card ? qs('#' + escCssIdent(triggerId), card) : null;
    var inner = qs('[data-selected-weight-panel-inner]', panel);
    if (!(inner instanceof HTMLElement)) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var duration = reduced ? 0 : 240;

    stopPanelAnimation(panel);
    panel.dataset.expanded = expanded ? 'true' : 'false';
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (panelRow instanceof HTMLElement) {
      if (hostedInItem) {
        panelRow.hidden = true;
        panelRow.setAttribute('aria-hidden', 'true');
      } else {
        panelRow.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      }
    }
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = false;
      panel.hidden = false;
      if (!duration) {
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        return;
      }
      inner.style.overflow = 'hidden';
      inner.style.height = '0px';
      inner.style.opacity = '0';
      panel.offsetHeight;
      var openTo = inner.scrollHeight;
      inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
      inner.style.height = openTo + 'px';
      inner.style.opacity = '1';
      var onOpenEnd = function onOpenEnd(ev) {
        if (ev.target !== inner || ev.propertyName !== 'height') return;
        inner.removeEventListener('transitionend', onOpenEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        panel.__b2bPanelAnimation = null;
      };
      inner.addEventListener('transitionend', onOpenEnd);
      panel.__b2bPanelAnimation = {
        cancel: function () {
          inner.removeEventListener('transitionend', onOpenEnd);
          inner.style.transition = '';
          inner.style.height = '';
          inner.style.opacity = '';
          inner.style.overflow = '';
        },
      };
      return;
    }

    if (!duration) {
      panel.hidden = true;
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = true;
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      if (opts && opts.restoreFocus && trigger instanceof HTMLElement) trigger.focus();
      return;
    }

    var closeFrom = inner.scrollHeight;
    inner.style.overflow = 'hidden';
    inner.style.height = closeFrom + 'px';
    inner.style.opacity = '1';
    panel.offsetHeight;
    inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
    inner.style.height = '0px';
    inner.style.opacity = '0';
    var onCloseEnd = function onCloseEnd(ev) {
      if (ev.target !== inner || ev.propertyName !== 'height') return;
      inner.removeEventListener('transitionend', onCloseEnd);
      panel.hidden = true;
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = true;
      inner.style.transition = '';
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      panel.__b2bPanelAnimation = null;
      if (opts && opts.restoreFocus && trigger instanceof HTMLElement) trigger.focus();
    };
    inner.addEventListener('transitionend', onCloseEnd);
    panel.__b2bPanelAnimation = {
      cancel: function () {
        inner.removeEventListener('transitionend', onCloseEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
      },
    };
  }

  /** @param {HTMLElement} row @param {boolean} expanded @param {{restoreFocus?: boolean}} [opts] */
  function setSelectedWeightPanelState(row, expanded, opts) {
    var rootEl = row.closest('[data-b2b-cart-review]');
    if (rootEl instanceof HTMLElement) syncSelectedWeightPanelPlacement(rootEl);
    var ui = getWeightUiEls(row);
    if (!(ui.panel instanceof HTMLElement)) return;
    row.dataset.selectedWeightOpen = expanded ? '1' : '0';
    animatePanelState(ui.panel, expanded, opts);
  }

  /**
   * @param {HTMLElement} row
   * @param {{ grouped: { weight: number, quantity: number }[], totalCount: number }} normalized
   * @param {number} totalWeight
   */
  function renderSelectedWeightPanel(row, normalized, totalWeight) {
    var ui = getWeightUiEls(row);
    var trigger = ui.trigger;
    var triggerCount = ui.triggerCount;
    var panelRow = ui.panelRow;
    var panel = ui.panel;
    if (!(trigger instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

    var titleCount = qs('[data-selected-weight-summary-count]', panel);
    var columnsHost = qs('[data-selected-weight-columns]', panel);
    if (!(columnsHost instanceof HTMLElement)) return;

    if (normalized.totalCount <= 0 || !normalized.grouped.length) {
      setSelectedWeightPanelState(row, false);
      trigger.hidden = true;
      trigger.setAttribute('aria-hidden', 'true');
      if (panelRow instanceof HTMLElement) {
        panelRow.hidden = true;
        panelRow.setAttribute('aria-hidden', 'true');
      }
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      return;
    }

    trigger.hidden = false;
    trigger.removeAttribute('aria-hidden');
    if (triggerCount) triggerCount.textContent = String(normalized.totalCount);
    if (titleCount) titleCount.textContent = String(normalized.totalCount);

    var columns = splitIntoWeightColumns(normalized.grouped, SELECTED_WEIGHT_ROWS_PER_TABLE);
    var colCount = columns.length;
    columnsHost.classList.toggle('b2b-cart-review__selected-weight-columns--single', colCount === 1);
    columnsHost.classList.toggle('b2b-cart-review__selected-weight-columns--multi', colCount > 1);
    columnsHost.style.setProperty('--bcr-weight-col-count', String(Math.min(4, Math.max(1, colCount))));

    var srStart = 1;
    columnsHost.innerHTML = columns
      .map(function (column) {
        var html = renderSelectedWeightColumnHtml(column, srStart);
        srStart += column.length;
        return html;
      })
      .join('');

    if (row.dataset.selectedWeightOpen === '1') setSelectedWeightPanelState(row, true);
  }

  /** @param {Record<string, unknown>} item */
  function weightStatsFromItem(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    var weight = props.Weight != null ? String(props.Weight).trim() : '';
    var total = 0;
    var min = Infinity;
    var max = -Infinity;
    var count = 0;

    if (weight) {
      splitCsv(weight).forEach(function (part) {
        var n = parseWeightNumber(part);
        if (n == null || !Number.isFinite(n) || n <= 0) return;
        count += 1;
        total += n;
        min = Math.min(min, n);
        max = Math.max(max, n);
      });
    }

    if (!count) {
      var grams = Number(item.grams || 0);
      var qty = Number(item.quantity || 0);
      if (Number.isFinite(grams) && grams > 0 && Number.isFinite(qty) && qty > 0) {
        total = (grams * qty) / 1000;
      }
    }

    return {
      count: count,
      min: count ? min : 0,
      max: count ? max : 0,
      total: total,
    };
  }

  /** @param {unknown} raw @param {boolean} [alreadyCents] */
  function parseMoneyCents(raw, alreadyCents) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      if (alreadyCents) return Math.round(raw);
      return Math.round(raw * 100);
    }
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return 0;
    var cleaned = s.replace(/[^0-9.,-]/g, '');
    if (!cleaned) return 0;
    var normalized = cleaned;
    if (cleaned.indexOf(',') >= 0 && cleaned.indexOf('.') >= 0) {
      normalized = cleaned.replace(/,/g, '');
    } else if (cleaned.indexOf(',') >= 0 && cleaned.indexOf('.') < 0) {
      normalized = cleaned.replace(/,/g, '.');
    }
    var num = Number(normalized);
    if (!Number.isFinite(num)) return 0;
    if (alreadyCents) return Math.round(num);
    return Math.round(num * 100);
  }

  /**
   * @param {Record<string, unknown>} item
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourSourceFromItem(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};

    var centKeys = [
      'LabourChargeCents',
      'Labour_Cents',
      'labor_charge_cents',
      'labour_charge_cents',
      'making_charge_cents',
    ];
    for (var i = 0; i < centKeys.length; i++) {
      var ck = centKeys[i];
      if (props[ck] != null && String(props[ck]).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(props[ck], true) };
      }
    }

    var moneyKeys = [
      'Labour Charge',
      'Labour',
      'LabourCharge',
      'labor_charge',
      'Labor Charge',
      'making_charge',
      'Making Charge',
    ];
    for (var j = 0; j < moneyKeys.length; j++) {
      var mk = moneyKeys[j];
      if (props[mk] != null && String(props[mk]).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(props[mk], false) };
      }
    }

    if (item.labour_charge_cents != null && String(item.labour_charge_cents).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labour_charge_cents, true) };
    }
    if (item.labor_charge_cents != null && String(item.labor_charge_cents).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labor_charge_cents, true) };
    }
    if (item.labour_charge != null && String(item.labour_charge).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labour_charge, false) };
    }
    if (item.labor_charge != null && String(item.labor_charge).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labor_charge, false) };
    }
    return { hasValue: false, cents: 0 };
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourSource(item, rowEl) {
    var primary = labourSourceFromItem(item);
    if (primary.hasValue) return primary;
    if (rowEl instanceof HTMLElement) {
      var fromLineData = rowEl.dataset.lineLabourCents;
      if (fromLineData != null && String(fromLineData).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromLineData, true) };
      }
      var fromVariantData = rowEl.dataset.variantLabourCents;
      if (fromVariantData != null && String(fromVariantData).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromVariantData, true) };
      }
      var lineLabourRaw = rowEl.dataset.lineLabour;
      if (lineLabourRaw != null && String(lineLabourRaw).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(lineLabourRaw, false) };
      }
    }
    return primary;
  }

  /**
   * Shopify admin-configured unit price for this cart line variant.
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function variantUnitPriceSource(item, rowEl) {
    var keys = ['original_price', 'price', 'final_price'];
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var raw = item[k];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return { hasValue: true, cents: Math.round(raw) };
      }
      if (raw != null && String(raw).trim() !== '') {
        var parsed = parseMoneyCents(raw, true);
        if (Number.isFinite(parsed)) return { hasValue: true, cents: parsed };
      }
    }
    if (rowEl instanceof HTMLElement) {
      var fromRow = rowEl.dataset.variantUnitPriceCents;
      if (fromRow != null && String(fromRow).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromRow, true) };
      }
    }
    return { hasValue: false, cents: 0 };
  }

  function getGoldPricing() {
    return typeof window !== 'undefined' ? window.AoneGoldPricing : undefined;
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   */
  function jewelleryMaterialFromItem(item, rowEl) {
    var goldPricing = getGoldPricing();
    if (goldPricing && typeof goldPricing.resolveCartRowMaterial === 'function') {
      var resolved = goldPricing.resolveCartRowMaterial({
        properties:
          item.properties && typeof item.properties === 'object'
            ? /** @type {Record<string, unknown>} */ (item.properties)
            : {},
        options_with_values: item.options_with_values,
        row: rowEl || null,
        productTitle: item.product_title || item.title || '',
      });
      return resolved || '';
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   */
  function goldPropertiesFromSources(item, rowEl) {
    /** @type {Record<string, unknown>} */
    var props =
      item.properties && typeof item.properties === 'object'
        ? Object.assign({}, /** @type {Record<string, unknown>} */ (item.properties))
        : {};
    if (rowEl instanceof HTMLElement) {
      if (rowEl.dataset.goldPricePerTroyOz) props._gold_price_per_troy_oz = rowEl.dataset.goldPricePerTroyOz;
      if (rowEl.dataset.goldPriceCapturedAt) props._gold_price_captured_at = rowEl.dataset.goldPriceCapturedAt;
      if (rowEl.dataset.goldPriceMaterial) props._gold_price_material = rowEl.dataset.goldPriceMaterial;
      if (rowEl.dataset.goldPriceCurrency) props._gold_price_currency = rowEl.dataset.goldPriceCurrency;
      if (rowEl.dataset.goldPriceUnit) props._gold_price_unit = rowEl.dataset.goldPriceUnit;
    }
    return props;
  }

  var LINE_ADDED_AT_STORAGE_KEY = 'bcr_line_added_at_v1';

  /** @returns {Record<string, string>} */
  function readLineAddedAtMap() {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
      var raw = window.localStorage.getItem(LINE_ADDED_AT_STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? /** @type {Record<string, string>} */ (parsed) : {};
    } catch {
      return {};
    }
  }

  /** @param {Record<string, string>} map */
  function writeLineAddedAtMap(map) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(LINE_ADDED_AT_STORAGE_KEY, JSON.stringify(map));
    } catch {
      /* ignore quota errors */
    }
  }

  /** @param {Record<string, unknown>} props */
  function readAddedAtFromProperties(props) {
    if (!props || typeof props !== 'object') return '';
    var keys = ['_gold_price_captured_at', '_cart_added_at', '_added_at', 'added_at'];
    for (var i = 0; i < keys.length; i += 1) {
      var val = props[keys[i]];
      if (val != null && String(val).trim()) return String(val).trim();
    }
    return '';
  }

  /** @param {string} value */
  function isValidIsoTimestamp(value) {
    if (!value) return false;
    var d = new Date(value);
    return !Number.isNaN(d.getTime());
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function resolveLineAddedAtIso(item, rowEl) {
    var props =
      item && item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    if (rowEl instanceof HTMLElement) {
      props = goldPropertiesFromSources(item || {}, rowEl);
    }

    var fromProps = readAddedAtFromProperties(props);
    if (isValidIsoTimestamp(fromProps)) return fromProps;

    var key =
      item && item.key != null
        ? String(item.key)
        : rowEl instanceof HTMLElement
          ? rowEl.dataset.lineKey || ''
          : '';
    if (key) {
      var map = readLineAddedAtMap();
      if (map[key] && isValidIsoTimestamp(String(map[key]))) return String(map[key]);
    }

    if (rowEl instanceof HTMLElement && rowEl.dataset.lineAddedAt && isValidIsoTimestamp(rowEl.dataset.lineAddedAt)) {
      return rowEl.dataset.lineAddedAt;
    }

    return '';
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function ensureLineAddedAtIso(item, rowEl) {
    var existing = resolveLineAddedAtIso(item, rowEl);
    if (existing) return existing;

    var key =
      item && item.key != null
        ? String(item.key)
        : rowEl instanceof HTMLElement
          ? rowEl.dataset.lineKey || ''
          : '';
    if (!key) return '';

    var iso = new Date().toISOString();
    var map = readLineAddedAtMap();
    map[key] = iso;
    writeLineAddedAtMap(map);

    if (rowEl instanceof HTMLElement) {
      rowEl.dataset.lineAddedAt = iso;
    }

    return iso;
  }

  /**
   * @param {HTMLElement} root
   * @param {Record<string, unknown>[]} items
   * @param {Record<string, unknown>} cfg
   */
  function syncLineAddedAtMetadata(root, items, cfg) {
    if (!Array.isArray(items)) return;

    root.__bcrLineAddedAtPersist = root.__bcrLineAddedAtPersist || {};
    if (!root.__bcrLineAddedAtPersistQueue || typeof root.__bcrLineAddedAtPersistQueue.then !== 'function') {
      root.__bcrLineAddedAtPersistQueue = Promise.resolve(null);
    }
    var persistQueue = /** @type {Array<() => Promise<unknown>>} */ ([]);

    items.forEach(function (item, idx) {
      var key = String(item.key || '');
      if (!key) return;

      var row = root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(key) + '"]');
      var rowEl = row instanceof HTMLElement ? row : null;
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var fromProps = readAddedAtFromProperties(props);
      var iso = fromProps && isValidIsoTimestamp(fromProps) ? fromProps : ensureLineAddedAtIso(item, rowEl);

      if (rowEl && iso) {
        rowEl.dataset.lineAddedAt = iso;
      }

      if (!fromProps && iso && !root.__bcrLineAddedAtPersist[key] && cfg) {
        root.__bcrLineAddedAtPersist[key] = true;
        persistQueue.push(function () {
          return persistLineAddedAtProperty(idx + 1, item, iso, cfg, root);
        });
      }
    });

    if (persistQueue.length) {
      persistQueue.forEach(function (runNext) {
        enqueueCartWrite(root, function () {
          return runNext();
        });
      });
    }
  }

  /**
   * @param {number} line
   * @param {Record<string, unknown>} item
   * @param {string} iso
   * @param {Record<string, unknown>} cfg
   * @param {HTMLElement} root
   */
  async function persistLineAddedAtProperty(line, item, iso, cfg, root) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? Object.assign({}, /** @type {Record<string, unknown>} */ (item.properties))
        : {};
    if (readAddedAtFromProperties(props)) return null;

    props._cart_added_at = iso;
    var qty = Number(item.quantity || 1);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;
    var lineKey = item.key != null ? String(item.key) : '';
    var payload = lineKey
      ? { id: lineKey, quantity: qty, properties: props }
      : { line: line, quantity: qty, properties: props };

    var attempts = 0;
    while (attempts < 2) {
      attempts += 1;
      try {
        var result = await fetchJson(String(cfg.cartChangeJsUrl || '/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        var cart = /** @type {Record<string, unknown>} */ (result.data);
        if (result.res.status === 409 && attempts < 2) {
          await waitMs(120);
          continue;
        }
        if (!result.res.ok || cart.errors || !Array.isArray(cart.items)) return null;
        adoptAuthoritativeLineKeys(root, cart);
        return cart;
      } catch {
        if (attempts < 2) {
          await waitMs(120);
          continue;
        }
        return null;
      }
    }

    return null;
  }

  /**
   * Latest catalogue Labour/gm (variant unit price) fetched for the cart page.
   * Cart line original_price/price can lag behind admin/app pricing updates until
   * a cart write; this map holds the authoritative live catalogue cents by variant id.
   * @param {HTMLElement} [rowEl]
   * @returns {Record<string, number>}
   */
  function getLiveLabourMap(rowEl) {
    var root =
      rowEl instanceof HTMLElement
        ? rowEl.closest('[data-b2b-cart-review]')
        : typeof document !== 'undefined'
          ? document.querySelector('[data-b2b-cart-review]')
          : null;
    if (!(root instanceof HTMLElement)) return {};
    if (!root.__b2bLiveLabourByVariantId || typeof root.__b2bLiveLabourByVariantId !== 'object') {
      root.__b2bLiveLabourByVariantId = {};
    }
    return /** @type {Record<string, number>} */ (root.__b2bLiveLabourByVariantId);
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function variantIdFromSources(item, rowEl) {
    if (item && item.variant_id != null && String(item.variant_id).trim() !== '') {
      return String(item.variant_id);
    }
    if (rowEl instanceof HTMLElement && rowEl.dataset.variantId) {
      return String(rowEl.dataset.variantId);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function liveLabourSource(item, rowEl) {
    var variantId = variantIdFromSources(item, rowEl);
    if (!variantId) return { hasValue: false, cents: 0 };
    var map = getLiveLabourMap(rowEl);
    var cents = map[variantId];
    if (typeof cents === 'number' && Number.isFinite(cents)) {
      return { hasValue: true, cents: Math.round(cents) };
    }
    return { hasValue: false, cents: 0 };
  }

  /**
   * Labour Per GM for a cart row.
   * Preferred source: latest catalogue variant unit price (live refresh on cart page).
   * Fallback: Shopify cart line unit price (may be stale until cart write),
   * then optional line-item labour properties.
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourChargeForRow(item, rowEl) {
    var fromLive = liveLabourSource(item, rowEl);
    if (fromLive.hasValue) return fromLive;
    var fromVariant = variantUnitPriceSource(item, rowEl);
    if (fromVariant.hasValue) return fromVariant;
    return labourSource(item, rowEl);
  }

  /**
   * Variant tariff % map (from Liquid data-tariff-percentage / custom.tariff_percentage).
   * Used so sync/validate paths without a row element can still resolve SKU tariff.
   * @param {HTMLElement} [rowEl]
   * @returns {Record<string, number>}
   */
  function getTariffPercentageMap(rowEl) {
    var root =
      rowEl instanceof HTMLElement
        ? rowEl.closest('[data-b2b-cart-review]')
        : typeof document !== 'undefined'
          ? document.querySelector('[data-b2b-cart-review]')
          : null;
    if (!(root instanceof HTMLElement)) return {};
    if (!root.__b2bTariffByVariantId || typeof root.__b2bTariffByVariantId !== 'object') {
      root.__b2bTariffByVariantId = {};
    }
    return /** @type {Record<string, number>} */ (root.__b2bTariffByVariantId);
  }

  /**
   * Seed / refresh the variant tariff map from cart row data attributes.
   * @param {HTMLElement} root
   */
  function seedTariffPercentageMapFromDom(root) {
    if (!(root instanceof HTMLElement)) return;
    var map = getTariffPercentageMap(root);
    root.querySelectorAll('[data-cart-item][data-variant-id]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var vid = String(row.dataset.variantId || '').trim();
      if (!vid) return;
      var raw = row.dataset.tariffPercentage;
      if (raw == null || String(raw).trim() === '') {
        delete map[vid];
        return;
      }
      var goldPricing = getGoldPricing();
      var parsed =
        goldPricing && typeof goldPricing.parseFinitePositiveNumber === 'function'
          ? goldPricing.parseFinitePositiveNumber(raw, false)
          : Number(String(raw).replace(/[^\d.-]/g, ''));
      if (parsed != null && Number.isFinite(parsed) && parsed >= 0) {
        map[vid] = parsed;
      } else {
        delete map[vid];
      }
    });
  }

  /**
   * Per-SKU tariff % for gold uplift only. Missing/empty → 0.
   * Never applied to labour in this theme path.
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {number}
   */
  function tariffPercentageForRow(item, rowEl) {
    var goldPricing = getGoldPricing();
    var parsePct = function (raw) {
      if (raw == null || String(raw).trim() === '') return null;
      if (goldPricing && typeof goldPricing.parseFinitePositiveNumber === 'function') {
        return goldPricing.parseFinitePositiveNumber(raw, false);
      }
      var n = Number(String(raw).replace(/[^\d.-]/g, ''));
      return Number.isFinite(n) && n >= 0 ? n : null;
    };

    if (rowEl instanceof HTMLElement) {
      var fromRow = parsePct(rowEl.dataset.tariffPercentage);
      if (fromRow != null) {
        var vidFromRow = variantIdFromSources(item, rowEl);
        if (vidFromRow) getTariffPercentageMap(rowEl)[vidFromRow] = fromRow;
        return fromRow;
      }
    }

    var variantId = variantIdFromSources(item, rowEl);
    if (variantId) {
      var mapped = getTariffPercentageMap(rowEl)[variantId];
      if (typeof mapped === 'number' && Number.isFinite(mapped) && mapped >= 0) return mapped;
    }

    if (item && item.properties && typeof item.properties === 'object') {
      var props = /** @type {Record<string, unknown>} */ (item.properties);
      var fromProp = parsePct(
        props._tariff_percentage != null
          ? props._tariff_percentage
          : props.tariff_percentage != null
            ? props.tariff_percentage
            : props.TariffPercentage
      );
      if (fromProp != null) return fromProp;
    }

    return 0;
  }

  /** Poll while cart page is visible so labour tracks external pricing changes. */
  var LIVE_LABOUR_POLL_MS = 12000;
  var LIVE_LABOUR_REFRESH_DEBOUNCE_MS = 220;

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function productHandleFromSources(item, rowEl) {
    if (item && item.handle != null && String(item.handle).trim()) {
      return String(item.handle).trim();
    }
    if (rowEl instanceof HTMLElement && rowEl.dataset.productHandle) {
      return String(rowEl.dataset.productHandle).trim();
    }
    var url =
      item && item.url != null
        ? String(item.url)
        : rowEl instanceof HTMLElement && rowEl.dataset.productUrl
          ? String(rowEl.dataset.productUrl)
          : '';
    if (url) {
      var match = url.match(/\/products\/([^/?#]+)/i);
      if (match && match[1]) {
        try {
          return decodeURIComponent(match[1]);
        } catch {
          return match[1];
        }
      }
    }
    if (rowEl instanceof HTMLElement) {
      var group = rowEl.closest('[data-product-group]');
      if (group instanceof HTMLElement && group.dataset.productHandle) {
        return String(group.dataset.productHandle).trim();
      }
    }
    return '';
  }

  /**
   * @param {string} handle
   * @returns {Promise<Record<string, unknown>|null>}
   */
  async function fetchProductJsonByHandle(handle) {
    var safe = String(handle || '').trim();
    if (!safe) return null;
    var url = '/products/' + encodeURIComponent(safe) + '.js?t=' + String(Date.now());
    try {
      var result = await fetchJson(url, { method: 'GET', cache: 'no-store' });
      if (!result.res.ok || !result.data || typeof result.data !== 'object') return null;
      return /** @type {Record<string, unknown>} */ (result.data);
    } catch {
      return null;
    }
  }

  /**
   * Batch-fetch latest variant unit prices (Labour/gm) for unique parent products
   * in the cart. One /products/{handle}.js request per unique handle — never N+1 per line.
   *
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cfg
   * @param {Record<string, unknown>[]} [items]
   * @param {string} [reason]
   * @returns {Promise<boolean>} true when pricing map changed and UI was recalculated
   */
  async function refreshLiveLabourPricing(root, cfg, items, reason) {
    if (!(root instanceof HTMLElement) || !root.isConnected) return false;
    if (document.hidden) return false;

    var cartItems = Array.isArray(items)
      ? items
      : Array.isArray(root.__b2bLastCartItems)
        ? root.__b2bLastCartItems
        : [];
    if (!cartItems.length) {
      root.__b2bLiveLabourByVariantId = {};
      return false;
    }

    root.__b2bLiveLabourSeq = (root.__b2bLiveLabourSeq || 0) + 1;
    var seq = root.__b2bLiveLabourSeq;

    /** @type {Map<string, string>} handle -> first seen */
    var handleSet = {};
    cartItems.forEach(function (item) {
      var key = String(item.key || '');
      var row = key
        ? root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(key) + '"]')
        : null;
      var rowEl = row instanceof HTMLElement ? row : null;
      var handle = productHandleFromSources(item, rowEl);
      if (handle) handleSet[handle] = handle;
    });

    var handles = Object.keys(handleSet);
    if (!handles.length) return false;

    var products = await Promise.all(
      handles.map(function (handle) {
        return fetchProductJsonByHandle(handle);
      })
    );

    if (seq !== root.__b2bLiveLabourSeq) return false;

    /** @type {Record<string, number>} */
    var nextMap = {};
    products.forEach(function (product) {
      if (!product || !Array.isArray(product.variants)) return;
      product.variants.forEach(function (variant) {
        if (!variant || typeof variant !== 'object') return;
        var v = /** @type {Record<string, unknown>} */ (variant);
        var vid = v.id != null ? String(v.id) : '';
        if (!vid) return;
        var priceRaw = v.price;
        var cents =
          typeof priceRaw === 'number' && Number.isFinite(priceRaw)
            ? Math.round(priceRaw)
            : priceRaw != null && String(priceRaw).trim() !== ''
              ? parseMoneyCents(priceRaw, true)
              : NaN;
        if (Number.isFinite(cents)) nextMap[vid] = cents;
      });
    });

    var prevMap =
      root.__b2bLiveLabourByVariantId && typeof root.__b2bLiveLabourByVariantId === 'object'
        ? /** @type {Record<string, number>} */ (root.__b2bLiveLabourByVariantId)
        : {};

    /** Only keep variants currently in the cart. */
    /** @type {Record<string, number>} */
    var pruned = {};
    var changed = false;
    cartItems.forEach(function (item) {
      var vid = item.variant_id != null ? String(item.variant_id) : '';
      if (!vid || nextMap[vid] == null) return;
      pruned[vid] = nextMap[vid];
      if (prevMap[vid] !== pruned[vid]) changed = true;

      // Keep in-memory cart items aligned so sync/validate paths also use live labour.
      item.original_price = pruned[vid];
      item.price = pruned[vid];
      item.final_price = pruned[vid];

      var key = String(item.key || '');
      if (!key) return;
      var row = root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(key) + '"]');
      if (row instanceof HTMLElement) {
        row.dataset.variantUnitPriceCents = String(pruned[vid]);
        var handle = productHandleFromSources(item, row);
        if (handle) row.dataset.productHandle = handle;
      }
    });

    Object.keys(prevMap).forEach(function (vid) {
      if (pruned[vid] == null) changed = true;
    });

    root.__b2bLiveLabourByVariantId = pruned;
    root.__b2bLastCartItems = cartItems;

    if (!changed) {
      if (typeof console !== 'undefined' && typeof console.debug === 'function') {
        console.debug('[b2b-cart] live labour unchanged', { reason: reason || 'refresh', variants: Object.keys(pruned).length });
      }
      return false;
    }

    recalculateCartPricing(root, cfg, cartItems);
    return true;
  }

  /**
   * Deduped/debounced entry point for live labour refresh.
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cfg
   * @param {string} [reason]
   */
  function scheduleLiveLabourRefresh(root, cfg, reason) {
    if (!(root instanceof HTMLElement)) return;
    root.__b2bLiveLabourPendingReason = reason || 'scheduled';
    if (!root.__b2bScheduleLiveLabour || typeof root.__b2bScheduleLiveLabour !== 'function') {
      root.__b2bScheduleLiveLabour = debounced(function () {
        if (!(root instanceof HTMLElement) || !root.isConnected) return;
        if (root.__b2bLiveLabourInFlight) {
          root.__b2bLiveLabourNeedsRerun = true;
          return;
        }
        var runReason = String(root.__b2bLiveLabourPendingReason || 'scheduled');
        root.__b2bLiveLabourInFlight = true;
        refreshLiveLabourPricing(root, cfg, root.__b2bLastCartItems || [], runReason)
          .catch(function () {
            /* keep last known labour; next focus/poll retries */
          })
          .then(function () {
            root.__b2bLiveLabourInFlight = false;
            if (root.__b2bLiveLabourNeedsRerun) {
              root.__b2bLiveLabourNeedsRerun = false;
              scheduleLiveLabourRefresh(root, cfg, 'coalesced');
            }
          });
      }, LIVE_LABOUR_REFRESH_DEBOUNCE_MS);
    }
    root.__b2bScheduleLiveLabour();
  }

  /**
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cfg
   */
  function startLiveLabourPolling(root, cfg) {
    stopLiveLabourPolling(root);
    var tick = function () {
      if (!(root instanceof HTMLElement) || !root.isConnected) {
        stopLiveLabourPolling(root);
        return;
      }
      if (document.hidden) return;
      var hasItems = !!qs('[data-cart-item]', root);
      if (!hasItems) return;
      scheduleLiveLabourRefresh(root, cfg, 'poll');
    };
    root.__b2bLiveLabourPollId = window.setInterval(tick, LIVE_LABOUR_POLL_MS);
  }

  /** @param {HTMLElement} root */
  function stopLiveLabourPolling(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.__b2bLiveLabourPollId) {
      window.clearInterval(root.__b2bLiveLabourPollId);
      root.__b2bLiveLabourPollId = 0;
    }
  }

  /**
   * @param {HTMLElement} rowEl
   * @param {Record<string, unknown>} pricingResult
   * @param {string} code
   * @param {ReturnType<typeof getGoldPricing>} goldPricing
   * @param {Record<string, unknown>} [item]
   */
  function renderGoldPriceCell(rowEl, pricingResult, code, goldPricing, item) {
    var cell = qs('[data-line-gold-price]', rowEl);
    if (!cell) return;
    var rateEl = qs('[data-gold-price-rate]', cell);
    var tipBtn = qs('[data-gold-price-row-tip]', cell);
    var tipEl = qs('[data-gold-price-timestamp]', cell);
    if (!(rateEl instanceof HTMLElement)) return;

    var gold = /** @type {Record<string, unknown>} */ (pricingResult.gold || {});
    var karat =
      (goldPricing && typeof goldPricing.normalizeGoldKarat === 'function'
        ? goldPricing.normalizeGoldKarat(gold.karat || gold.material || rowEl.dataset.jewelryMaterial || '')
        : null) ||
      rowEl.dataset.goldKarat ||
      '';
    if (karat) rowEl.dataset.goldKarat = String(karat);

    var updatedAt =
      pricingResult.goldRateUpdatedAt != null
        ? String(pricingResult.goldRateUpdatedAt)
        : gold.updatedAt != null
          ? String(gold.updatedAt)
          : goldPricing && goldPricing.getStoreSnapshot
            ? String((goldPricing.getStoreSnapshot().lastUpdated || '') )
            : '';

    function hideGoldPriceTip() {
      if (tipEl instanceof HTMLElement) tipEl.textContent = '';
      if (tipBtn instanceof HTMLElement) {
        tipBtn.hidden = true;
        tipBtn.setAttribute('aria-hidden', 'true');
      }
    }

    function showGoldPriceTip(lines) {
      if (!(tipEl instanceof HTMLElement) || !(tipBtn instanceof HTMLElement)) return;
      tipEl.textContent = lines.join('\n');
      tipBtn.hidden = false;
      tipBtn.removeAttribute('aria-hidden');
    }

    if (pricingResult.status === 'loading') {
      setPriceSkeleton(rateEl, 'cell');
      hideGoldPriceTip();
      cell.dataset.goldPricingFallback = '0';
      cell.removeAttribute('aria-label');
      rowEl.removeAttribute('data-pricing-unavailable');
      return;
    }

    if (
      pricingResult.status === 'ok' &&
      pricingResult.goldPricePerGram != null &&
      Number.isFinite(Number(pricingResult.goldPricePerGram)) &&
      goldPricing
    ) {
      var goldPerGram = Number(pricingResult.goldPricePerGram);
      setPriceText(
        rateEl,
        goldPricing.formatPerGram
          ? goldPricing.formatPerGram(goldPerGram, code)
          : goldPricing.formatMoney(Math.round(goldPerGram * 100), code, false) + '/g'
      );

      var tipParts = [];
      if (updatedAt && goldPricing.formatCapturedTimestamp) {
        var fullTs = goldPricing.formatCapturedTimestamp(updatedAt).replace(/^Added on\s+/i, '');
        if (fullTs) tipParts.push('Updated: ' + fullTs);
      } else if (updatedAt && goldPricing.formatLiveTickerTimestamp) {
        var liveTs = goldPricing.formatLiveTickerTimestamp(updatedAt);
        if (liveTs) tipParts.push('Updated: ' + liveTs);
      }

      if (tipParts.length) {
        showGoldPriceTip(tipParts);
        cell.setAttribute('aria-label', tipParts.join('. '));
      } else {
        hideGoldPriceTip();
        cell.removeAttribute('aria-label');
      }
      cell.dataset.goldPricingFallback = '0';
      rowEl.removeAttribute('data-pricing-unavailable');
      return;
    }

    setPriceText(rateEl, 'Unavailable');
    hideGoldPriceTip();
    cell.dataset.goldPricingFallback = '0';
    cell.setAttribute('aria-label', formatLivePricingUnavailableMessage());
    rowEl.dataset.pricingUnavailable = '1';
  }

  /**
   * @param {HTMLElement} rowEl
   * @param {Record<string, unknown>} pricingResult
   * @param {string} code
   * @param {boolean} withCode
   * @param {ReturnType<typeof getGoldPricing>} goldPricing
   */
  function renderLabourUnitAndTotalCells(rowEl, pricingResult, code, withCode, goldPricing) {
    var labourDisplay = qs('[data-line-labour-display]', rowEl);
    var unitPriceCell = qs('[data-line-unit-price]', rowEl);
    var priceCell = qs('[data-line-price]', rowEl);

    if (labourDisplay instanceof HTMLElement) {
      if (pricingResult.labourPerGram != null && Number.isFinite(Number(pricingResult.labourPerGram)) && goldPricing) {
        labourDisplay.textContent = goldPricing.formatMoney(
          Math.round(Number(pricingResult.labourPerGram) * 100),
          code,
          withCode
        );
      }
    }

    if (unitPriceCell instanceof HTMLElement) {
      unitPriceCell.classList.remove('b2b-cart-review__value--unavailable');
      if (pricingResult.status === 'loading') {
        setPriceSkeleton(unitPriceCell, 'cell');
      } else if (
        pricingResult.status === 'ok' &&
        pricingResult.unitPricePerGram != null &&
        Number.isFinite(Number(pricingResult.unitPricePerGram)) &&
        goldPricing
      ) {
        setPriceText(
          unitPriceCell,
          goldPricing.formatPerGram
            ? goldPricing.formatPerGram(Number(pricingResult.unitPricePerGram), code)
            : goldPricing.formatMoney(Math.round(Number(pricingResult.unitPricePerGram) * 100), code, false) + '/g'
        );
      } else {
        setPriceText(unitPriceCell, formatUnavailableMoneyLabel());
        unitPriceCell.classList.add('b2b-cart-review__value--unavailable');
      }
    }

    if (priceCell instanceof HTMLElement) {
      priceCell.classList.remove('b2b-cart-review__value--unavailable');
      if (pricingResult.status === 'loading') {
        setPriceSkeleton(priceCell, 'cell');
        delete rowEl.dataset.calculatedRowTotalCents;
      } else if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null && goldPricing) {
        setPriceText(priceCell, goldPricing.formatMoney(pricingResult.rowTotalCents, code, withCode));
        rowEl.dataset.calculatedRowTotalCents = String(pricingResult.rowTotalCents);
      } else {
        setPriceText(priceCell, formatUnavailableMoneyLabel());
        priceCell.classList.add('b2b-cart-review__value--unavailable');
        delete rowEl.dataset.calculatedRowTotalCents;
      }
    }
  }

  function resolveRowWeightGrams(item, rowEl) {
    if (item) {
      var stats = weightStatsFromItem(item);
      if (Number.isFinite(stats.total) && stats.total > 0) return stats.total;
    }
    if (rowEl instanceof HTMLElement) {
      if (rowEl.dataset.selectedWeight) {
        var fromProp = weightStatsFromItem({ properties: { Weight: rowEl.dataset.selectedWeight } });
        if (Number.isFinite(fromProp.total) && fromProp.total > 0) return fromProp.total;
      }
      var totalWeightCell = qs('[data-row-total-weight]', rowEl);
      var goldPricing = getGoldPricing();
      var parsedFromDom = goldPricing
        ? goldPricing.parseWeightInGrams(totalWeightCell ? totalWeightCell.textContent : '')
        : parseWeightNumber(totalWeightCell ? totalWeightCell.textContent : '');
      if (parsedFromDom != null && parsedFromDom > 0) return parsedFromDom;
    }
    return null;
  }

  function resolveRowLabourCents(item, rowEl) {
    var labour = labourChargeForRow(item || {}, rowEl);
    if (labour.hasValue && Number.isFinite(labour.cents)) {
      return { cents: labour.cents, resolved: true };
    }
    return { cents: null, resolved: false };
  }

  function formatUnavailableMoneyLabel() {
    return 'Unavailable';
  }

  function formatLivePricingUnavailableMessage() {
    return 'Live gold pricing is currently unavailable. Please refresh or try again.';
  }

  /**
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cfg
   * @param {Record<string, unknown>[]} [items]
   */
  function recalculateCartPricing(root, cfg, items) {
    var goldPricing = getGoldPricing();
    seedTariffPercentageMapFromDom(root);
    var code =
      typeof cfg.currencyCode === 'string' && cfg.currencyCode
        ? cfg.currencyCode
        : goldPricing
          ? goldPricing.getActiveCurrencyCode()
          : 'USD';
    var withCode = !!cfg.currencyCodeEnabled;
    var cartItems = Array.isArray(items) ? items : root.__b2bLastCartItems || [];
    if (Array.isArray(items)) root.__b2bLastCartItems = items;

    /** @type {Record<string, Record<string, unknown>>} */
    var itemByKey = {};
    cartItems.forEach(function (item) {
      var key = String(item.key || '');
      if (key) itemByKey[key] = item;
    });

    /** @type {Record<string, { qty: number, weight: number, subtotal: number }>} */
    var byProduct = {};
    var orderTotalCents = 0;
    var invalidCount = 0;
    var loadingCount = 0;
    var qtyGrand = 0;
    var weightGrand = 0;
    var debugPricing =
      typeof window !== 'undefined' && /(?:^|[?&])bcr_debug=1(?:&|$)/.test(window.location.search || '');

    /** @type {Record<string, number>} */
    var srByProduct = {};

    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var key = row.dataset.lineKey || '';
      var item = key && itemByKey[key] ? itemByKey[key] : null;
      var props = goldPropertiesFromSources(item || {}, row);
      var material = jewelleryMaterialFromItem(item || {}, row);
      var karat =
        goldPricing && typeof goldPricing.normalizeGoldKarat === 'function'
          ? goldPricing.normalizeGoldKarat(material || row.dataset.jewelryMaterial || '')
          : null;
      if (karat) row.dataset.goldKarat = karat;

      var pid = row.dataset.productId || '';
      if (pid) {
        srByProduct[pid] = (srByProduct[pid] || 0) + 1;
        row.dataset.rowSr = String(srByProduct[pid]);
        var srCell = qs('[data-row-sr-display]', row);
        if (srCell) srCell.textContent = String(srByProduct[pid]);
      }

      var totalWeightGrams = resolveRowWeightGrams(item, row);
      if (totalWeightGrams != null && Number.isFinite(totalWeightGrams)) {
        totalWeightGrams = roundMoney2(totalWeightGrams);
      }
      var labourInfo = resolveRowLabourCents(item, row);

      /** @type {Record<string, unknown>} */
      var pricingResult = { status: 'unavailable', gold: { status: 'unavailable' } };
      if (goldPricing) {
        pricingResult = goldPricing.calculateRowPricing({
          properties: props,
          rowMaterial: material,
          totalWeightGrams: totalWeightGrams,
          labourCents: labourInfo.cents,
          labourResolved: labourInfo.resolved,
          cartCurrency: code,
          preferLive: true,
          allowFallback: false,
          tariffPercentage: tariffPercentageForRow(item || {}, row),
        });
      }

      if (pricingResult.status === 'loading') {
        loadingCount += 1;
        row.removeAttribute('data-pricing-unavailable');
      } else if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null) {
        row.removeAttribute('data-pricing-unavailable');
      } else {
        invalidCount += 1;
        row.dataset.pricingUnavailable = '1';
      }

      renderLabourUnitAndTotalCells(row, pricingResult, code, withCode, goldPricing);
      renderGoldPriceCell(row, pricingResult, code, goldPricing, item || undefined);

      if (debugPricing && typeof console !== 'undefined' && typeof console.debug === 'function') {
        var gold = pricingResult.gold || {};
        console.debug('[SyncCart Pricing]', {
          lineKey: key,
          productTitle: item && (item.product_title || item.title),
          resolvedMaterial: material,
          goldKarat: karat,
          liveGoldPricePerGram: pricingResult.goldPricePerGram,
          labourPerGram: pricingResult.labourPerGram,
          unitPricePerGram: pricingResult.unitPricePerGram,
          totalWeight: pricingResult.totalWeightGrams != null ? pricingResult.totalWeightGrams : totalWeightGrams,
          rowTotalCents: pricingResult.rowTotalCents,
          liveGoldPricePerTroyOz: gold.perTroyOz,
          result: pricingResult.status,
        });
      }

      var qty = Number(item && item.quantity != null ? item.quantity : row.dataset.quantity || 0);
      if (!Number.isFinite(qty)) qty = 0;
      qtyGrand += qty;
      var lineWeightForTotals =
        pricingResult.totalWeightGrams != null && Number.isFinite(Number(pricingResult.totalWeightGrams))
          ? Number(pricingResult.totalWeightGrams)
          : totalWeightGrams != null && Number.isFinite(totalWeightGrams)
            ? totalWeightGrams
            : 0;
      weightGrand = roundMoney2(weightGrand + lineWeightForTotals);

      if (pid) {
        if (!byProduct[pid]) byProduct[pid] = { qty: 0, weight: 0, subtotal: 0 };
        var metric = byProduct[pid];
        metric.qty += qty;
        metric.weight = roundMoney2(metric.weight + lineWeightForTotals);
        if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null) {
          metric.subtotal += pricingResult.rowTotalCents;
          orderTotalCents += pricingResult.rowTotalCents;
        }
      }
    });

    root.querySelectorAll('[data-group-metrics]').forEach(function (metricEl) {
      if (!(metricEl instanceof HTMLElement)) return;
      var pid = metricEl.dataset.groupProductId || '';
      var metric = byProduct[pid] || { qty: 0, weight: 0, subtotal: 0 };
      var qtyEl = qs('[data-group-total-qty]', metricEl);
      var weightEl = qs('[data-group-total-weight]', metricEl);
      var subtotalEl = qs('[data-group-subtotal-cents]', metricEl);
      if (qtyEl) qtyEl.textContent = String(metric.qty);
      if (weightEl) weightEl.textContent = metric.weight > 0 ? metric.weight.toFixed(2) + ' g' : '-';
      if (subtotalEl) {
        subtotalEl.classList.remove('b2b-cart-review__metric-value--unavailable');
        if (loadingCount > 0) {
          setPriceSkeleton(subtotalEl, 'metric');
        } else if (metric.subtotal > 0 && goldPricing) {
          setPriceText(subtotalEl, goldPricing.formatMoney(metric.subtotal, code, withCode));
        } else if (invalidCount > 0) {
          setPriceText(subtotalEl, formatUnavailableMoneyLabel());
          subtotalEl.classList.add('b2b-cart-review__metric-value--unavailable');
        } else {
          setPriceText(
            subtotalEl,
            goldPricing ? goldPricing.formatMoney(0, code, withCode) : formatMoney(0, code, withCode)
          );
        }
      }
    });

    var totalQtyEl = qs('[data-order-summary-total-qty]', root);
    if (totalQtyEl) totalQtyEl.textContent = String(qtyGrand);

    var totalWeightEl = qs('[data-order-summary-total-weight]', root);
    if (totalWeightEl) {
      totalWeightEl.textContent = weightGrand > 0 ? weightGrand.toFixed(2) + ' g' : '-';
    }

    var totalEl = qs('[data-order-summary-total]', root);
    if (totalEl) {
      totalEl.classList.remove('b2b-cart-review__metric-value--unavailable');
      if (loadingCount > 0) {
        setPriceSkeleton(totalEl, 'total');
      } else if (invalidCount > 0) {
        setPriceText(totalEl, '-');
        totalEl.classList.add('b2b-cart-review__metric-value--unavailable');
      } else if (goldPricing) {
        setPriceText(totalEl, goldPricing.formatMoney(orderTotalCents, code, withCode));
      } else {
        setPriceText(totalEl, formatMoney(orderTotalCents, code, withCode));
      }
    }

    var warnEl = qs('[data-order-pricing-warning]', root);
    if (warnEl instanceof HTMLElement) {
      if (invalidCount > 0 && loadingCount === 0) {
        warnEl.textContent = formatLivePricingUnavailableMessage();
        warnEl.hidden = false;
        warnEl.setAttribute('aria-hidden', 'false');
      } else {
        warnEl.hidden = true;
        warnEl.setAttribute('aria-hidden', 'true');
      }
    }

    root.__b2bPricingInvalid = invalidCount > 0;
    root.__b2bPricingLoading = loadingCount > 0;
    root.__b2bComputedOrderTotalCents = invalidCount > 0 || loadingCount > 0 ? 0 : orderTotalCents;
    updateSalesRepGate(root, cfg);
  }

  /**
   * @param {Record<string, unknown>} cart
   * @param {Record<string, unknown>} cfg
   * @param {HTMLElement} [root]
   */
  function validateCartPricing(cart, cfg, root) {
    var items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return 'Your cart is empty.';
    var goldPricing = getGoldPricing();
    if (!goldPricing) return 'Gold pricing is unavailable. Please refresh and try again.';

    if (root instanceof HTMLElement) {
      seedTariffPercentageMapFromDom(root);
    }

    var code =
      typeof cfg.currencyCode === 'string' && cfg.currencyCode
        ? cfg.currencyCode
        : goldPricing.getActiveCurrencyCode();

    for (var i = 0; i < items.length; i++) {
      var item = /** @type {Record<string, unknown>} */ (items[i]);
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var material = jewelleryMaterialFromItem(item);
      var totalWeightGrams = resolveRowWeightGrams(item);
      var labourInfo = resolveRowLabourCents(item);
      var pricingResult = goldPricing.calculateRowPricing({
        properties: props,
        rowMaterial: material,
        totalWeightGrams: totalWeightGrams,
        labourCents: labourInfo.cents,
        labourResolved: labourInfo.resolved,
        cartCurrency: code,
        preferLive: true,
        allowFallback: false,
        tariffPercentage: tariffPercentageForRow(item),
      });
      if (pricingResult.status === 'loading') {
        return 'Gold pricing is still loading. Please wait a moment and try again.';
      }
      if (pricingResult.status !== 'ok') {
        return formatLivePricingUnavailableMessage();
      }
    }

    if (root instanceof HTMLElement && (root.__b2bPricingInvalid || root.__b2bPricingLoading)) {
      return formatLivePricingUnavailableMessage();
    }
    return null;
  }

  /** @param {HTMLElement} root */
  function getProductDetailsStateStore(root) {
    var existing = root.__b2bOpenProductDetails;
    if (existing && typeof existing === 'object') return existing;
    root.__b2bOpenProductDetails = {};
    return root.__b2bOpenProductDetails;
  }

  /** @param {HTMLButtonElement} toggle @param {boolean} expanded */
  function syncProductDetailsToggleVisual(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var label = qs('.b2b-cart-review__details-toggle-label', toggle);
    if (label) label.textContent = expanded ? 'Hide Details' : 'Open Details';
  }

  /** @param {HTMLElement} root @param {boolean} disabled */
  function setProductDetailsButtonsDisabled(root, disabled) {
    root.querySelectorAll('[data-product-details-toggle]').forEach(function (btn) {
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.disabled = disabled;
      if (disabled) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  /** @param {HTMLElement} panel */
  function stopProductDetailsAnimation(panel) {
    var running = panel.__b2bProductDetailsAnimation;
    if (running && typeof running.cancel === 'function') running.cancel();
    panel.__b2bProductDetailsAnimation = null;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} expanded
   * @param {{ immediate?: boolean }} [opts]
   */
  function setProductDetailsState(card, expanded, opts) {
    var toggle = qs('[data-product-details-toggle]', card);
    var panel = qs('[data-product-details]', card);
    var inner = qs('[data-product-details-inner]', panel || card);
    if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;

    var root = card.closest('[data-b2b-cart-review]');
    if (root instanceof HTMLElement) {
      var pid = card.dataset.productId || '';
      if (pid) {
        var store = getProductDetailsStateStore(root);
        if (expanded) store[pid] = 1;
        else delete store[pid];
      }
    }

    syncProductDetailsToggleVisual(toggle, expanded);
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    card.dataset.detailsOpen = expanded ? '1' : '0';

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var immediate = !!(opts && opts.immediate) || reduced;
    var duration = immediate ? 0 : 240;

    stopProductDetailsAnimation(panel);

    if (expanded) {
      panel.hidden = false;
      if (!duration) {
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        return;
      }
      inner.style.overflow = 'hidden';
      inner.style.height = '0px';
      inner.style.opacity = '0';
      panel.offsetHeight;
      var openTo = inner.scrollHeight;
      inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
      inner.style.height = openTo + 'px';
      inner.style.opacity = '1';
      var onOpenEnd = function onOpenEnd(ev) {
        if (ev.target !== inner || ev.propertyName !== 'height') return;
        inner.removeEventListener('transitionend', onOpenEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        panel.__b2bProductDetailsAnimation = null;
      };
      inner.addEventListener('transitionend', onOpenEnd);
      panel.__b2bProductDetailsAnimation = {
        cancel: function () {
          inner.removeEventListener('transitionend', onOpenEnd);
          inner.style.transition = '';
          inner.style.height = '';
          inner.style.opacity = '';
          inner.style.overflow = '';
        },
      };
      return;
    }

    if (!duration) {
      panel.hidden = true;
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      return;
    }

    var closeFrom = inner.scrollHeight;
    inner.style.overflow = 'hidden';
    inner.style.height = closeFrom + 'px';
    inner.style.opacity = '1';
    panel.offsetHeight;
    inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
    inner.style.height = '0px';
    inner.style.opacity = '0';
    var onCloseEnd = function onCloseEnd(ev) {
      if (ev.target !== inner || ev.propertyName !== 'height') return;
      inner.removeEventListener('transitionend', onCloseEnd);
      panel.hidden = true;
      inner.style.transition = '';
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      panel.__b2bProductDetailsAnimation = null;
    };
    inner.addEventListener('transitionend', onCloseEnd);
    panel.__b2bProductDetailsAnimation = {
      cancel: function () {
        inner.removeEventListener('transitionend', onCloseEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
      },
    };
  }

  /** @param {HTMLElement} root */
  function reconcileProductDetails(root) {
    var state = getProductDetailsStateStore(root);
    /** @type {Record<string, 1>} */
    var alive = {};

    root.querySelectorAll('[data-product-group]').forEach(function (card) {
      if (!(card instanceof HTMLElement)) return;
      var pid = String(card.dataset.productId || '').trim();
      if (pid) alive[pid] = 1;
      var shouldOpen = !!(pid && state[pid] === 1);
      setProductDetailsState(card, shouldOpen, { immediate: true });
    });

    Object.keys(state).forEach(function (pid) {
      if (!alive[pid]) delete state[pid];
    });
  }

  /**
   * Sync Order validates SKU + Weight + Qty only.
   * SerialNumbers may exist on the line as a stale Shopify hint and are ignored by the backend.
   * @param {Record<string, unknown>} cart
   * @returns {string|null}
   */
  function validateCartLinesForSync(cart) {
    var items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return 'Your cart is empty.';
    for (var i = 0; i < items.length; i++) {
      var item = /** @type {Record<string, unknown>} */ (items[i]);
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var weight = props.Weight != null ? String(props.Weight).trim() : '';
      var qty = Number(item.quantity || 0);
      if (!Number.isInteger(qty) || qty <= 0) {
        return 'Each cart item must have a quantity greater than 0 before syncing.';
      }
      if (!weight) {
        return 'Select a weight for every item before syncing.';
      }
      var weightParts = splitCsv(weight);
      if (weightParts.length !== 1 && weightParts.length !== qty) {
        return 'Weight selections must match quantity for each item.';
      }
      for (var w = 0; w < weightParts.length; w++) {
        if (parseWeightNumber(weightParts[w]) == null) {
          return 'One or more items have an invalid weight.';
        }
      }
    }
    return null;
  }

  /** @returns {string} */
  function createSyncRequestId() {
    var rand = '';
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return 'sync_' + crypto.randomUUID().replace(/-/g, '');
      }
    } catch (_) {}
    rand = String(Math.random()).slice(2, 10) + String(Date.now());
    return 'sync_' + rand;
  }

  /**
   * @param {Record<string, unknown>} data
   * @param {Record<string, unknown>} cfg
   * @returns {string}
   */
  function formatSyncErrorMessage(data, cfg) {
    var fallback = String(cfg.syncErrorFallback || 'Unable to sync order');
    var code = data && (data.errorCode || data.code) != null ? String(data.errorCode || data.code) : '';
    var details = data && Array.isArray(data.details) ? data.details : [];
    var firstDetail = details.length ? details[0] : null;
    var items =
      firstDetail && firstDetail.items && Array.isArray(firstDetail.items)
        ? firstDetail.items
        : firstDetail && firstDetail.sku
          ? [firstDetail]
          : [];

    if (code === 'INSUFFICIENT_AVAILABILITY' || code === 'ALLOCATION_CONFLICT') {
      var Availability = typeof window !== 'undefined' ? window.B2bCartSyncAvailability : undefined;
      if (Availability && typeof Availability.extractAvailabilityConflicts === 'function') {
        var structured = Availability.extractAvailabilityConflicts(data);
        if (structured && structured.length) {
          return typeof data.message === 'string' && data.message
            ? data.message
            : 'Some items are no longer available. Please update the cart before syncing.';
        }
      }
      if (items.length) {
        var lines = items.map(function (it) {
          var sku = it.sku != null ? String(it.sku) : 'Item';
          var weight = it.weight != null ? String(it.weight) : '';
          var unit = it.weightUnit != null ? String(it.weightUnit) : 'g';
          var requested = it.requestedQuantity != null ? Number(it.requestedQuantity) : null;
          var available = it.availableQuantity != null ? Number(it.availableQuantity) : null;
          var label = weight ? sku + ' – ' + weight + unit : sku;
          if (requested != null && available != null) {
            return label + '\nRequested: ' + requested + '\nCurrently available: ' + available;
          }
          return label;
        });
        return lines.join('\n\n') + '\n\nPlease update the cart before syncing.';
      }
      return typeof data.message === 'string' && data.message
        ? data.message
        : 'Some items are no longer available. Please update the cart before syncing.';
    }

    if (code === 'WMS_UNAVAILABLE') {
      return typeof data.message === 'string' && data.message
        ? data.message
        : 'Inventory could not be verified. Please try again.';
    }

    if (code === 'CART_EXPIRED') {
      return 'This cart is no longer active. Please choose another cart.';
    }

    if (code === 'CART_NOT_EDITABLE') {
      return 'This cart can no longer be edited. Please choose another cart.';
    }

    return typeof data.message === 'string' && data.message ? data.message : fallback;
  }

  /** @param {Record<string, unknown>} cfg */
  function showToast(cfg, message, type) {
    var Toast = typeof window !== 'undefined' ? window.AonegndToast : undefined;
    if (Toast && typeof Toast.show === 'function') {
      Toast.show(message, type);
      return;
    }
    if (type === 'error' && cfg.syncErrorFallback) {
      window.alert(message || String(cfg.syncErrorFallback));
    }
  }

  /** @param {Record<string, unknown>} item */
  function lineImageUrl(item) {
    if (typeof item.image === 'string') return item.image;
    if (item.image && typeof item.image === 'object' && 'src' in item.image) {
      return String(/** @type {{src?: string}} */ (item.image).src || '');
    }
    var fi = item.featured_image;
    if (typeof fi === 'string') return fi;
    if (fi && typeof fi === 'object' && 'url' in fi)
      return String(/** @type {{url?: string}} */ (fi).url || '');
    return '';
  }

  /** @param {Record<string, unknown>} item */
  function mapLineItem(item) {
    return {
      product_id: item.product_id,
      variant_id: item.variant_id,
      sku: item.sku != null ? String(item.sku) : '',
      title: item.title,
      product_title: item.product_title,
      variant_title: item.variant_title,
      quantity: item.quantity,
      price: item.price,
      final_price: item.final_price != null ? item.final_price : item.price,
      line_price: item.line_price,
      final_line_price: item.final_line_price != null ? item.final_line_price : item.line_price,
      image: lineImageUrl(item),
      url: item.url,
      properties: item.properties,
    };
  }

  /** @param {Record<string, unknown>} cart @param {Record<string, unknown>} cfg @param {string} idem @param {{id:number|string,name:string}|null} [selectedCustomer] @param {HTMLElement} [root] */
  function buildSyncPayload(cart, cfg, idem, selectedCustomer, root) {
    var token = readToken(cfg);
    var profile = readProfile(cfg);

    if (root instanceof HTMLElement) {
      seedTariffPercentageMapFromDom(root);
    }

    var isoCurrency =
      typeof cart.currency === 'string'
        ? cart.currency
        : cart.currency &&
            typeof cart.currency === 'object' &&
            cart.currency.iso_code != null &&
            typeof cart.currency.iso_code === 'string'
          ? cart.currency.iso_code
          : cfg.currencyCode;

    var items = Array.isArray(cart.items) ? cart.items.map(function (it) {
      var mapped = mapLineItem(/** @type {Record<string, unknown>} */ (it));
      var source = /** @type {Record<string, unknown>} */ (it);
      var props =
        source.properties && typeof source.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (source.properties)
          : {};
      var goldPricing = getGoldPricing();
      if (goldPricing) {
        var material = jewelleryMaterialFromItem(source);
        var weights = weightStatsFromItem(source);
        var labour = labourChargeForRow(source);
        var pricingResult = goldPricing.calculateRowPricing({
          properties: props,
          rowMaterial: material,
          totalWeightGrams: weights.total,
          labourCents: labour.hasValue ? labour.cents : 0,
          labourResolved: labour.hasValue,
          cartCurrency: isoCurrency || String(cfg.currencyCode || 'USD'),
          preferLive: true,
          allowFallback: false,
          tariffPercentage: tariffPercentageForRow(source),
        });
        mapped.computed_row_total_cents =
          pricingResult.status === 'ok' && pricingResult.rowTotalCents != null ? pricingResult.rowTotalCents : null;
        mapped.gold_karat =
          goldPricing.normalizeGoldKarat
            ? goldPricing.normalizeGoldKarat(material)
            : material;
        mapped.live_gold_price_per_gram =
          pricingResult.status === 'ok' && pricingResult.goldPricePerGram != null
            ? pricingResult.goldPricePerGram
            : null;
        mapped.labour_per_gram =
          pricingResult.labourPerGram != null
            ? pricingResult.labourPerGram
            : labour.hasValue
              ? roundMoney2(labour.cents / 100)
              : null;
        mapped.unit_price_per_gram =
          pricingResult.status === 'ok' && pricingResult.unitPricePerGram != null
            ? pricingResult.unitPricePerGram
            : null;
        mapped.selected_weight_total_grams =
          pricingResult.status === 'ok' && pricingResult.totalWeightGrams != null
            ? pricingResult.totalWeightGrams
            : roundMoney2(weights.total);
        mapped.gold_rate_updated_at =
          pricingResult.goldRateUpdatedAt != null ? String(pricingResult.goldRateUpdatedAt) : '';
        mapped.captured_gold_price_per_troy_oz = props._gold_price_per_troy_oz != null ? String(props._gold_price_per_troy_oz) : '';
        mapped.captured_gold_price_material = props._gold_price_material != null ? String(props._gold_price_material) : material;
        mapped.captured_gold_price_captured_at =
          props._gold_price_captured_at != null ? String(props._gold_price_captured_at) : '';
      }
      return mapped;
    }) : [];

    var customerRaw = selectedCustomer || cfg.shopCustomer;
    /** @type {{id: string, email: string, name: string} | null} */
    var custOut = null;
    if (customerRaw && typeof customerRaw === 'object') {
      var c = /** @type {Record<string, unknown>} */ (customerRaw);
      custOut = {
        id: c.id != null ? String(c.id) : '',
        email: c.email != null ? String(c.email) : '',
        name: c.name != null ? String(c.name) : '',
      };
    }

    /** @type {Record<string, unknown>} */
    var out = {
      source: 'shopify_theme_cart',
      idempotency_key: idem,
      shopify_cart_token: cart.token,
      currency: isoCurrency || cart.currency,
      subtotal_price: cart.items_subtotal_price,
      total_price: cart.total_price,
      // TODO: Backend must recalculate order totals from captured gold rate, selected weight, material, and labour - do not trust browser totals alone.
      computed_total_cents:
        root instanceof HTMLElement && typeof root.__b2bComputedOrderTotalCents === 'number'
          ? root.__b2bComputedOrderTotalCents
          : null,
      item_count: cart.item_count,
      note: cart.note,
      attributes: cart.attributes || {},
      sales_rep: {
        token_present: !!token,
        profile: profile || {},
      },
      customer: custOut,
      items: items,
    };
    return out;
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cfg */
  function updateSalesRepGate(root, cfg) {
    var ok = isSalesRepOk(cfg);
    var warn = qs('[data-b2b-login-warning]', root);
    if (warn) {
      warn.hidden = !cfg.salesRepValidate || ok;
      warn.setAttribute('aria-hidden', warn.hidden ? 'true' : 'false');
    }
    var btn = qs('[data-sync-button]', root);
    if (!btn || !(btn instanceof HTMLButtonElement)) return;
    var hasItems = !!qs('[data-cart-item]', root);
    if (!hasItems) {
      btn.disabled = true;
      return;
    }
    if (cfg.salesRepValidate && cfg.disableSyncWhenNotLogged && !ok) {
      btn.disabled = true;
      return;
    }
    if (root.__b2bPricingInvalid || root.__b2bPricingLoading) {
      btn.disabled = true;
      return;
    }
    btn.disabled = false;
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>[]} items @param {Record<string, unknown>} cfg */
  function applyComputedMetrics(root, items, cfg) {
    recalculateCartPricing(root, cfg, items);
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>[]} items @param {string} code @param {boolean} withCode @param {number} subtotalCents @param {number} totalCents */
  function applyComputedMetricsLegacy(root, items, code, withCode, subtotalCents, totalCents) {
    /** @type {Record<string, { qty: number, weight: number, labour: number, subtotal: number }>} */
    var byProduct = {};
    var labourGrand = 0;
    var qtyGrand = 0;
    var weightGrand = 0;

    items.forEach(function (item) {
      var pid = String(item.product_id || '');
      if (!pid) return;
      if (!byProduct[pid]) byProduct[pid] = { qty: 0, weight: 0, labour: 0, subtotal: 0 };
      var m = byProduct[pid];
      var qty = Number(item.quantity || 0);
      var lineSubtotal = Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);
      var weights = weightStatsFromItem(item);
      var labour = labourSourceFromItem(item).cents;

      m.qty += Number.isFinite(qty) ? qty : 0;
      m.weight += Number.isFinite(weights.total) ? weights.total : 0;
      m.labour += Number.isFinite(labour) ? labour : 0;
      m.subtotal += Number.isFinite(lineSubtotal) ? lineSubtotal : 0;
      labourGrand += Number.isFinite(labour) ? labour : 0;
      qtyGrand += Number.isFinite(qty) ? qty : 0;
      weightGrand += Number.isFinite(weights.total) ? weights.total : 0;
    });

    root.querySelectorAll('[data-group-metrics]').forEach(function (metricEl) {
      if (!(metricEl instanceof HTMLElement)) return;
      var pid = metricEl.dataset.groupProductId || '';
      var m = byProduct[pid] || { qty: 0, weight: 0, labour: 0, subtotal: 0 };
      var qtyEl = qs('[data-group-total-qty]', metricEl);
      var weightEl = qs('[data-group-total-weight]', metricEl);
      var labourEl = qs('[data-group-total-labour]', metricEl);
      var subtotalEl = qs('[data-group-subtotal-cents]', metricEl);

      if (qtyEl) qtyEl.textContent = String(m.qty);
      if (weightEl) weightEl.textContent = m.weight > 0 ? m.weight.toFixed(2) + ' g' : '-';
      if (labourEl) labourEl.textContent = m.labour > 0 ? formatMoney(m.labour, code, withCode) : '-';
      if (subtotalEl) subtotalEl.textContent = formatMoney(m.subtotal, code, withCode);
    });

    var labourSummary = qs('[data-order-summary-labour]', root);
    if (labourSummary) {
      labourSummary.textContent = labourGrand > 0 ? formatMoney(labourGrand, code, withCode) : '-';
    }

    var subtotalEl = qs('[data-order-summary-subtotal]', root);
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotalCents, code, withCode);

    var totalQtyEl = qs('[data-order-summary-total-qty]', root);
    if (totalQtyEl) totalQtyEl.textContent = String(qtyGrand);

    var totalWeightEl = qs('[data-order-summary-total-weight]', root);
    if (totalWeightEl) totalWeightEl.textContent = weightGrand > 0 ? weightGrand.toFixed(2) + ' g' : '-';

    var estimated = totalCents;
    if (labourGrand > 0) {
      if (Math.abs(totalCents - subtotalCents) <= 1) {
        estimated = subtotalCents + labourGrand;
      } else if (Math.abs(totalCents - (subtotalCents + labourGrand)) <= 1) {
        estimated = totalCents;
      } else {
        estimated = totalCents;
      }
    }
    var totalEl = qs('[data-order-summary-total]', root);
    if (totalEl) totalEl.textContent = formatMoney(estimated, code, withCode);
  }

  /**
   * Cart writes must run one at a time. Property persistence, quantity edits,
   * and removal all change Shopify line keys; overlapping requests were applying
   * a stale cart and wiping every row.
   * @param {HTMLElement} root
   * @param {() => Promise<unknown>} fn
   * @returns {Promise<unknown>}
   */
  function enqueueCartWrite(root, fn) {
    var previous = root.__b2bCartWriteQueue;
    if (!previous || typeof previous.then !== 'function') previous = Promise.resolve();
    var run = previous
      .catch(function () {
        return null;
      })
      .then(function () {
        root.__b2bCartWriteInFlight = true;
        root.__b2bCartMutationSeq = (root.__b2bCartMutationSeq || 0) + 1;
        return fn();
      })
      .then(function (value) {
        root.__b2bCartWriteInFlight = false;
        return value;
      })
      .catch(function (err) {
        root.__b2bCartWriteInFlight = false;
        throw err;
      });
    root.__b2bCartWriteQueue = run.catch(function () {
      return null;
    });
    return run;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} cart
   * @returns {boolean}
   */
  function isAuthoritativeCart(cart) {
    return !!cart && typeof cart === 'object' && Array.isArray(cart.items) && !cart.errors;
  }

  /**
   * @param {Record<string, unknown>} cfg
   * @param {Record<string, unknown>} payload
   */
  async function postCartChange(cfg, payload) {
    var url = String((cfg && cfg.cartChangeJsUrl) || '/cart/change.js');
    /** @type {{ res: Response, data: Record<string, unknown>, txt: string } | null} */
    var last = null;
    var attempts = 0;
    while (attempts < 2) {
      attempts += 1;
      last = await fetchJson(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (last.res.status === 409 && attempts < 2) {
        await waitMs(120);
        continue;
      }
      return last;
    }
    return last;
  }

  /** @param {unknown} value */
  function normalizeSignaturePart(value) {
    return String(value == null ? '' : value).replace(/\s+/g, '').toLowerCase();
  }

  /** @param {Record<string, unknown> | null | undefined} item */
  function itemProperties(item) {
    if (!item || !item.properties || typeof item.properties !== 'object') return {};
    return /** @type {Record<string, unknown>} */ (item.properties);
  }

  /** @param {Record<string, unknown>} props */
  function serialsFromProperties(props) {
    var keys = ['SerialNumbers', 'serialNumbers', 'SerialNumber', 'serial_numbers'];
    for (var i = 0; i < keys.length; i += 1) {
      if (props[keys[i]] != null && String(props[keys[i]]).trim()) return String(props[keys[i]]);
    }
    return '';
  }

  /** @param {Record<string, unknown>} item */
  function itemLineSignature(item) {
    var props = itemProperties(item);
    var sku = props.SKU != null ? props.SKU : item && item.sku != null ? item.sku : '';
    return [
      item && item.variant_id != null ? String(item.variant_id) : '',
      normalizeSignaturePart(sku),
      normalizeSignaturePart(extractRawSelectedWeight(item)),
      normalizeSignaturePart(serialsFromProperties(props)),
    ].join('|');
  }

  /** @param {HTMLElement} row */
  function rowLineSignature(row) {
    return [
      String(row.dataset.variantId || ''),
      normalizeSignaturePart(row.dataset.sku || ''),
      normalizeSignaturePart(row.dataset.selectedWeight || ''),
      normalizeSignaturePart(row.dataset.lineSerials || ''),
    ].join('|');
  }

  /** @param {string} signature */
  function signatureIsUseful(signature) {
    return String(signature || '')
      .split('|')
      .some(function (part) {
        return part !== '';
      });
  }

  /** Variant, SKU, and weight. Serials are compared only when both sides have them. */
  function signatureCore(signature) {
    var parts = String(signature || '').split('|');
    return [parts[0] || '', parts[1] || '', parts[2] || ''].join('|');
  }

  /** @param {string} core */
  function coreIsUseful(core) {
    return String(core || '')
      .split('|')
      .some(function (part) {
        return part !== '';
      });
  }

  /**
   * Match existing DOM rows to the authoritative cart.
   * Exact line keys win. If Shopify rotated a key (property writes do this),
   * variant + SKU + weight + serials identifies the same physical line.
   * @param {HTMLElement[]} rows
   * @param {Record<string, unknown>[]} items
   */
  function matchCartRowsToItems(rows, items) {
    var unmatched = rows.slice();
    /** @type {Array<{ row: HTMLElement, item: Record<string, unknown>, line: number }>} */
    var pairs = [];

    /** @param {(row: HTMLElement) => boolean} predicate */
    function take(predicate) {
      for (var i = 0; i < unmatched.length; i += 1) {
        if (predicate(unmatched[i])) return unmatched.splice(i, 1)[0];
      }
      return null;
    }

    items.forEach(function (item, idx) {
      var key = String(item.key || '');
      if (!key) return;
      var row = take(function (candidate) {
        return (candidate.dataset.lineKey || candidate.dataset.cartLineKey || '') === key;
      });
      if (row) pairs.push({ row: row, item: item, line: idx + 1 });
    });

    items.forEach(function (item, idx) {
      var already = pairs.some(function (pair) {
        return pair.item === item;
      });
      if (already) return;
      var signature = itemLineSignature(item);
      if (!signatureIsUseful(signature)) return;
      var row = take(function (candidate) {
        return rowLineSignature(candidate) === signature;
      });
      if (row) pairs.push({ row: row, item: item, line: idx + 1 });
    });

    items.forEach(function (item, idx) {
      var already = pairs.some(function (pair) {
        return pair.item === item;
      });
      if (already) return;
      var core = signatureCore(itemLineSignature(item));
      if (!coreIsUseful(core)) return;
      var pendingItems = items.filter(function (candidate) {
        var paired = pairs.some(function (pair) {
          return pair.item === candidate;
        });
        return !paired && signatureCore(itemLineSignature(candidate)) === core;
      });
      var pendingRows = unmatched.filter(function (candidate) {
        return signatureCore(rowLineSignature(candidate)) === core;
      });
      if (pendingItems.length !== 1 || pendingRows.length !== 1) return;
      var row = take(function (candidate) {
        return signatureCore(rowLineSignature(candidate)) === core;
      });
      if (row) pairs.push({ row: row, item: item, line: idx + 1 });
    });

    /** @type {Record<string, HTMLElement[]>} */
    var rowsByVariant = {};
    unmatched.forEach(function (row) {
      var variantId = String(row.dataset.variantId || '');
      if (!variantId) return;
      if (!rowsByVariant[variantId]) rowsByVariant[variantId] = [];
      rowsByVariant[variantId].push(row);
    });
    /** @type {Record<string, Record<string, unknown>[]>} */
    var itemsByVariant = {};
    items.forEach(function (item) {
      var paired = pairs.some(function (pair) {
        return pair.item === item;
      });
      if (paired) return;
      var variantId = item.variant_id != null ? String(item.variant_id) : '';
      if (!variantId) return;
      if (!itemsByVariant[variantId]) itemsByVariant[variantId] = [];
      itemsByVariant[variantId].push(item);
    });
    Object.keys(itemsByVariant).forEach(function (variantId) {
      var pendingItems = itemsByVariant[variantId];
      var pendingRows = rowsByVariant[variantId] || [];
      if (!pendingItems.length || pendingItems.length !== pendingRows.length) return;
      pendingItems.forEach(function (item) {
        var row = pendingRows.shift();
        if (!(row instanceof HTMLElement)) return;
        var index = unmatched.indexOf(row);
        if (index >= 0) unmatched.splice(index, 1);
        pairs.push({ row: row, item: item, line: items.indexOf(item) + 1 });
      });
    });

    return { pairs: pairs, removedRows: unmatched, assignedCount: pairs.length };
  }

  /**
   * @param {HTMLElement} row
   * @param {Record<string, unknown>} item
   */
  function writeRowLineKey(row, item) {
    var newKey = item.key != null ? String(item.key) : '';
    if (!newKey || !(row instanceof HTMLElement)) return;
    var oldKey = row.dataset.lineKey || row.dataset.cartLineKey || '';
    row.dataset.lineKey = newKey;
    row.dataset.cartLineKey = newKey;
    var removeBtn = row.querySelector('[data-remove-item]');
    if (removeBtn instanceof HTMLElement) removeBtn.dataset.lineKey = newKey;
    if (!oldKey || oldKey === newKey || !row.parentElement) return;
    var panelRow = row.parentElement.querySelector(
      '[data-selected-weight-panel-row][data-panel-for-line="' + escCssIdent(oldKey) + '"]'
    );
    if (panelRow instanceof HTMLElement) panelRow.dataset.panelForLine = newKey;
  }

  /** @param {HTMLElement} row */
  function removeCartRow(row) {
    if (!(row instanceof HTMLElement)) return;
    var removeUi = getWeightUiEls(row);
    if (removeUi.panel instanceof HTMLElement) removeUi.panel.remove();
    if (removeUi.panelRow instanceof HTMLElement) {
      removeUi.panelRow.remove();
    } else {
      var legacyPanelRow = row.nextElementSibling;
      if (legacyPanelRow instanceof HTMLElement && legacyPanelRow.matches('[data-selected-weight-panel-row]')) {
        legacyPanelRow.remove();
      }
    }
    row.remove();
  }

  /**
   * Keep DOM line keys aligned after a cart write that rotates keys
   * without removing a line (for example persisting _cart_added_at).
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cart
   */
  function adoptAuthoritativeLineKeys(root, cart) {
    if (!(root instanceof HTMLElement) || !isAuthoritativeCart(cart)) return;
    var items = cart.items.map(function (it) {
      return /** @type {Record<string, unknown>} */ (it);
    });
    root.__b2bLastCartItems = items;
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-cart-item]'));
    var matchResult = matchCartRowsToItems(rows, items);
    if (matchResult.assignedCount === 0 && items.length > 0 && rows.length > 0) return;
    matchResult.pairs.forEach(function (pair) {
      writeRowLineKey(pair.row, pair.item);
      pair.row.dataset.lineNumber = String(pair.line);
      var serialRaw = serialsFromProperties(itemProperties(pair.item));
      if (serialRaw) pair.row.dataset.lineSerials = serialRaw.trim();
    });
  }

  /**
   * DOM keys go stale after property writes. Prefer the current Shopify key
   * for the same variant, SKU, weight, and serials.
   * @param {HTMLElement} root
   * @param {string} domKey
   * @param {string} [knownSignature] Signature captured before this write was queued.
   * @returns {string}
   */
  function resolveLiveLineKey(root, domKey, knownSignature) {
    var key = String(domKey || '').trim();
    var items = Array.isArray(root.__b2bLastCartItems) ? root.__b2bLastCartItems : [];
    if (
      key &&
      items.some(function (item) {
        return String(item.key || '') === key;
      })
    ) {
      return key;
    }
    var signature = String(knownSignature || '');
    if (!signature && key) {
      var row = root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(key) + '"]');
      if (row instanceof HTMLElement) signature = rowLineSignature(row);
    }
    if (signatureIsUseful(signature)) {
      var matches = items.filter(function (item) {
        return itemLineSignature(item) === signature;
      });
      if (matches.length === 1 && matches[0].key != null) return String(matches[0].key);
      var core = signatureCore(signature);
      if (coreIsUseful(core)) {
        var loose = items.filter(function (item) {
          return signatureCore(itemLineSignature(item)) === core;
        });
        if (loose.length === 1 && loose[0].key != null) return String(loose[0].key);
      }
    }
    return key;
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cart @param {Record<string, unknown>} cfg */
  function applyCartToDom(root, cart, cfg) {
    if (!isAuthoritativeCart(cart)) return;
    var codeRaw = cart.currency;
    var code =
      typeof codeRaw === 'string'
        ? codeRaw
        : codeRaw && typeof codeRaw === 'object' && 'iso_code' in codeRaw
          ? String(/** @type {{ iso_code?: string }} */ (codeRaw).iso_code || '')
          : cfg.currencyCode;
    var withCode = !!cfg.currencyCodeEnabled;
    var subtotalCents = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : 0;
    var totalCents = typeof cart.total_price === 'number' ? cart.total_price : 0;
    var items = cart.items.map(function (it) {
      return /** @type {Record<string, unknown>} */ (it);
    });
    root.__b2bLastCartItems = items;
    var isEmptyCart = items.length === 0;
    var quantityCount = items.reduce(function (sum, item) {
      var qty = Number(item.quantity || 0);
      return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
    var count = isEmptyCart
      ? 0
      : typeof cart.item_count === 'number' && cart.item_count > 0
        ? cart.item_count
        : quantityCount;

    if (isEmptyCart && root.__b2bAvailabilityCtrl && typeof root.__b2bAvailabilityCtrl.reset === 'function') {
      root.__b2bAvailabilityCtrl.reset();
    }

    root.querySelectorAll('[data-b2b-cart-count]').forEach(function (countEl) {
      countEl.textContent = String(count);
    });

    var wrap = qs('[data-b2b-cart-count-wrap]', root);
    if (wrap) {
      wrap.hidden = count === 0;
      wrap.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }

    var mainTitle = qs('[data-b2b-main-title]', root);
    if (mainTitle) mainTitle.hidden = count === 0;
    var cartHeader = qs('[data-b2b-cart-header]', root);
    if (cartHeader) {
      cartHeader.hidden = count === 0;
      cartHeader.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }

    var domRows = Array.prototype.slice.call(root.querySelectorAll('[data-cart-item]')).filter(function (row) {
      return row instanceof HTMLElement;
    });
    var matchResult = matchCartRowsToItems(domRows, items);
    if (items.length > 0 && matchResult.assignedCount === 0 && domRows.length > 0) {
      matchResult.removedRows = [];
    }
    matchResult.removedRows.forEach(function (row) {
      removeCartRow(row);
    });

    matchResult.pairs.forEach(function (packed) {
      var row = packed.row;
      var item = packed.item;
      writeRowLineKey(row, item);
      row.dataset.lineNumber = String(packed.line);
      row.dataset.quantity = String(item.quantity != null ? item.quantity : 1);
      row.dataset.productId = String(item.product_id != null ? item.product_id : row.dataset.productId || '');
      if (item.variant_id != null) row.dataset.variantId = String(item.variant_id);
      var serialRaw = serialsFromProperties(itemProperties(item));
      if (serialRaw) row.dataset.lineSerials = serialRaw.trim();
      else delete row.dataset.lineSerials;
      var handle = productHandleFromSources(item, row);
      if (handle) row.dataset.productHandle = handle;
      var unitFromCart = variantUnitPriceSource(item, row);
      if (unitFromCart.hasValue) {
        // Keep DOM fallback fresh from cart.js when Shopify recalculates line prices.
        // Live catalogue refresh may overwrite this with newer admin/app pricing.
        row.dataset.variantUnitPriceCents = String(unitFromCart.cents);
      }

      var qty = Number(item.quantity || 0);
      var qtyCell = qs('[data-row-qty]', row);
      if (qtyCell) qtyCell.textContent = String(Number.isFinite(qty) ? qty : 0);

      var stats = weightStatsFromItem(item);
      var totalWeightCell = qs('[data-row-total-weight]', row);

      var rawSelectedWeight = extractRawSelectedWeight(item);
      if (rawSelectedWeight != null && String(rawSelectedWeight).trim()) {
        row.dataset.selectedWeight = String(rawSelectedWeight).trim();
      } else {
        delete row.dataset.selectedWeight;
      }
      var normalized = normalizeSelectedWeights(rawSelectedWeight || '');
      renderSelectedWeightPanel(row, normalized, Number.isFinite(stats.total) ? stats.total : 0);

      if (totalWeightCell) {
        totalWeightCell.textContent = stats.total > 0 ? stats.total.toFixed(2) + ' g' : '-';
      }

      var labourDisplay = qs('[data-line-labour-display]', row);
      var labour = labourChargeForRow(item, row);
      if (labourDisplay) {
        labourDisplay.textContent = labour.hasValue
          ? formatMoney(labour.cents, String(code || 'USD'), withCode)
          : '-';
      }

      var unitPriceCell = qs('[data-line-unit-price]', row);
      var priceCell = qs('[data-line-price]', row);
      var goldRateEl = qs('[data-gold-price-rate]', row);
      var goldTipBtn = qs('[data-gold-price-row-tip]', row);
      if (unitPriceCell && !row.dataset.calculatedRowTotalCents) {
        setPriceSkeleton(unitPriceCell, 'cell');
      }
      if (priceCell && !row.dataset.calculatedRowTotalCents) {
        setPriceSkeleton(priceCell, 'cell');
      }
      if (goldRateEl instanceof HTMLElement && !row.dataset.calculatedRowTotalCents) {
        setPriceSkeleton(goldRateEl, 'cell');
        if (goldTipBtn instanceof HTMLElement) {
          goldTipBtn.hidden = true;
          goldTipBtn.setAttribute('aria-hidden', 'true');
        }
      }
    });
    syncSelectedWeightPanelPlacement(root);

    root.querySelectorAll('[data-product-group]').forEach(function (groupEl) {
      if (!(groupEl instanceof HTMLElement)) return;
      var pid = groupEl.dataset.productId || '';
      if (!pid) return;
      var hasAny = !!groupEl.querySelector('[data-cart-item][data-product-id="' + escCssIdent(pid) + '"]');
      if (!hasAny) groupEl.remove();
    });

    reconcileProductDetails(root);

    syncLineAddedAtMetadata(root, items, cfg);

    root.classList.toggle('b2b-cart-review--has-items', !isEmptyCart);
    var layout = qs('[data-b2b-cart-layout]', root);
    var emptyShell = qs('[data-b2b-empty-shell]', root);
    var summarySidebar = qs('[data-b2b-summary-sidebar]', root);
    if (isEmptyCart) {
      if (layout) layout.hidden = true;
      if (emptyShell) emptyShell.hidden = false;
      if (summarySidebar) {
        summarySidebar.hidden = true;
        summarySidebar.setAttribute('aria-hidden', 'true');
      }
    } else {
      if (layout) layout.hidden = false;
      if (emptyShell) emptyShell.hidden = true;
      if (summarySidebar) {
        summarySidebar.hidden = false;
        summarySidebar.removeAttribute('aria-hidden');
      }
    }

    applyComputedMetrics(root, items, cfg);
    updateSalesRepGate(root, cfg);
    scheduleLiveLabourRefresh(root, cfg, 'cart-dom-apply');
  }

  /** @param {HTMLElement} root @param {string} text */
  function showCartFeedback(root, text) {
    var el = qs('[data-b2b-cart-feedback]', root);
    if (!el) return;
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.textContent = text;
    el.hidden = false;
    window.setTimeout(function () {
      if (el.textContent === text) el.hidden = true;
    }, 4500);
  }

  /** @param {Record<string, unknown>} cart @param {string} sourceId */
  function publishCartCountUpdate(cart, sourceId) {
    var bridge = typeof window !== 'undefined' ? window.aoneCartCount : null;
    if (bridge && typeof bridge.publish === 'function') {
      bridge.publish(cart, sourceId || 'b2b-cart-review');
      return;
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: sourceId || 'b2b-cart-review',
          data: { itemCount: typeof cart.item_count === 'number' ? cart.item_count : 0 },
        },
      })
    );
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cfg */
  function init(root, cfg) {
    var syncBtn = qs('[data-sync-button]', root);
    /** @type {any} */
    var availabilityCtrl = null;

    updateSalesRepGate(root, cfg);
    window.addEventListener('storage', function () {
      updateSalesRepGate(root, cfg);
    });

    /**
     * Change one line by its Shopify line-item key.
     * /cart/change.js targets that key only. /cart/update.js can treat the
     * variant id inside the key as the target and zero every line of that variant.
     * @param {number} line
     * @param {number} qty
     * @param {string} [lineKey]
     */
    async function changeLine(line, qty, lineKey) {
      var requestedKey = String(lineKey || '').trim();
      var signature = '';
      if (requestedKey) {
        var signatureRow = root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(requestedKey) + '"]');
        if (signatureRow instanceof HTMLElement) signature = rowLineSignature(signatureRow);
      }
      return enqueueCartWrite(root, async function () {
        root.classList.add('b2b-cart-review--updating');
        root.setAttribute('aria-busy', 'true');
        setProductDetailsButtonsDisabled(root, true);
        try {
          var liveKey = resolveLiveLineKey(root, requestedKey, signature);
          var payload = liveKey ? { id: liveKey, quantity: qty } : { line: line, quantity: qty };
          var result = await postCartChange(cfg, payload);
          var cart = result && result.data ? result.data : {};
          if (!result || !result.res || !result.res.ok || !isAuthoritativeCart(cart)) {
            var emsg =
              (typeof cart.message === 'string' && cart.message) ||
              (cart.description && String(cart.description)) ||
              (typeof cart.errors === 'string' && cart.errors) ||
              'Could not update cart.';
            showCartFeedback(root, emsg);
            return null;
          }
          applyCartToDom(root, cart, cfg);
          publishCartCountUpdate(cart, 'b2b-cart-review:line-change');
          return cart;
        } catch {
          showCartFeedback(root, 'Network error while updating cart.');
          return null;
        } finally {
          root.classList.remove('b2b-cart-review--updating');
          root.removeAttribute('aria-busy');
          setProductDetailsButtonsDisabled(root, false);
        }
      });
    }

    /**
     * Set quantities for specific line-item keys. Each key is sent on its own
     * /cart/change.js call so sibling lines, including same-variant lines with
     * different weights or serials, stay untouched.
     * @param {Record<string, number>} updates
     * @param {{rootBusy?: boolean, lineSnapshots?: Record<string, string>}} [opts]
     */
    async function updateCartByKeys(updates, opts) {
      if (!updates || !Object.keys(updates).length) return null;
      var busy = !opts || opts.rootBusy !== false;
      var lineSnapshots = opts && opts.lineSnapshots ? opts.lineSnapshots : {};
      return enqueueCartWrite(root, async function () {
        if (busy) {
          root.classList.add('b2b-cart-review--updating');
          root.setAttribute('aria-busy', 'true');
          setProductDetailsButtonsDisabled(root, true);
        }
        /** @type {Record<string, unknown> | null} */
        var cart = null;
        try {
          var keys = Object.keys(updates);
          for (var i = 0; i < keys.length; i += 1) {
            var liveKey = resolveLiveLineKey(root, keys[i], lineSnapshots[keys[i]] || '');
            var qty = Number(updates[keys[i]]);
            if (!Number.isFinite(qty) || qty < 0) qty = 0;
            if (!liveKey) {
              showCartFeedback(root, 'Could not identify the cart line to update.');
              if (cart) applyCartToDom(root, cart, cfg);
              return null;
            }
            var result = await postCartChange(cfg, { id: liveKey, quantity: qty });
            var nextCart = result && result.data ? result.data : {};
            if (!result || !result.res || !result.res.ok || !isAuthoritativeCart(nextCart)) {
              var err =
                (typeof nextCart.message === 'string' && nextCart.message) ||
                (typeof nextCart.description === 'string' && nextCart.description) ||
                (typeof nextCart.errors === 'string' && nextCart.errors) ||
                'Cart could not be updated.';
              showCartFeedback(root, err);
              if (cart) applyCartToDom(root, cart, cfg);
              return null;
            }
            cart = nextCart;
            root.__b2bLastCartItems = /** @type {Record<string, unknown>[]} */ (cart.items);
          }
          if (!cart) return null;
          applyCartToDom(root, cart, cfg);
          publishCartCountUpdate(cart, 'b2b-cart-review:key-update');
          return cart;
        } catch {
          showCartFeedback(root, 'Network error while updating cart.');
          if (cart) applyCartToDom(root, cart, cfg);
          return null;
        } finally {
          if (busy) {
            root.classList.remove('b2b-cart-review--updating');
            root.removeAttribute('aria-busy');
            setProductDetailsButtonsDisabled(root, false);
          }
        }
      });
    }

    /** @param {string} productId @param {HTMLButtonElement | null} [trigger] */
    async function removeGroup(productId, trigger) {
      var rows = root.querySelectorAll('[data-cart-item][data-product-id="' + escCssIdent(productId) + '"]');
      if (!rows.length) return;
      /** @type {Record<string, number>} */
      var updates = {};
      /** @type {Record<string, string>} */
      var lineSnapshots = {};
      rows.forEach(function (row) {
        if (!(row instanceof HTMLElement)) return;
        var key = row.dataset.lineKey || '';
        if (!key) return;
        updates[key] = 0;
        lineSnapshots[key] = rowLineSignature(row);
      });
      if (!Object.keys(updates).length) return;
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }
      try {
        var cart = await updateCartByKeys(updates, { lineSnapshots: lineSnapshots });
        if (cart) {
          showToast(cfg, String(cfg.removeSuccessMessage || 'The item is removed successfully'), 'success');
        }
      } finally {
        if (trigger) {
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
        }
      }
    }

    /** @param {string} lineKey @param {HTMLButtonElement} trigger */
    async function removeLineByKey(lineKey, trigger) {
      var key = String(lineKey || '').trim();
      if (!key) return;
      if (trigger.disabled) return;
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
      var sourceRow = trigger.closest('[data-cart-item]');
      /** @type {Record<string, string>} */
      var lineSnapshots = {};
      if (sourceRow instanceof HTMLElement) lineSnapshots[key] = rowLineSignature(sourceRow);
      try {
        /** @type {Record<string, number>} */
        var updates = {};
        updates[key] = 0;
        var cart = await updateCartByKeys(updates, { lineSnapshots: lineSnapshots });
        if (cart) {
          showToast(cfg, String(cfg.removeSuccessMessage || 'The item is removed successfully'), 'success');
        }
      } finally {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }

    if (
      typeof window !== 'undefined' &&
      window.B2bCartSyncAvailability &&
      typeof window.B2bCartSyncAvailability.createController === 'function'
    ) {
      availabilityCtrl = window.B2bCartSyncAvailability.createController({
        root: root,
        cfg: cfg,
        fetchJson: fetchJson,
        applyCartToDom: applyCartToDom,
        publishCartCountUpdate: publishCartCountUpdate,
        showCartFeedback: showCartFeedback,
        showToast: showToast,
        updateCartByKeys: updateCartByKeys,
      });
    }
    root.__b2bAvailabilityCtrl = availabilityCtrl;

    var debouncedInput = debounced(function (/** @type {HTMLInputElement} */ input) {
      var row = input.closest('[data-cart-item]');
      if (!(row instanceof HTMLElement)) return;
      var q = parseInt(input.value, 10);
      var fq = Number.isNaN(q) ? 1 : Math.max(1, q);
      input.value = String(fq);
      var line = parseInt(row.dataset.lineNumber || '0', 10);
      if (line) changeLine(line, fq, row.dataset.lineKey || '');
    }, 440);

    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-b2b-clear-cart-form]')) return;
      e.preventDefault();

      if (!cfg.confirmClearCart) {
        form.submit();
        return;
      }

      /** @type {HTMLElement | null} */
      var clearModalEl = null;
      var clearModalId = cfg.clearConfirmModalId != null ? String(cfg.clearConfirmModalId) : '';
      if (clearModalId) clearModalEl = document.getElementById(clearModalId);
      var ClearConfirm = typeof window !== 'undefined' ? window.AoneConfirmationModal : undefined;
      if (!clearModalEl || !ClearConfirm || typeof ClearConfirm.open !== 'function') {
        form.submit();
        return;
      }

      if (form.dataset.clearConfirmPending === '1') return;
      form.dataset.clearConfirmPending = '1';
      ClearConfirm.open(clearModalEl)
        .then(function (ok) {
          if (ok) form.submit();
        })
        .finally(function () {
          delete form.dataset.clearConfirmPending;
        });
    });

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var weightToggle = t.closest('[data-selected-weight-toggle]');
      var weightHide = t.closest('[data-selected-weight-hide]');
      var productDetailsToggle = t.closest('[data-product-details-toggle]');
      var minus = t.closest('[data-quantity-minus]');
      var plus = t.closest('[data-quantity-plus]');
      var removeBtn = t.closest('[data-remove-item]');
      var removeGroupBtn = t.closest('[data-remove-group]');

      if (weightToggle && weightToggle instanceof HTMLButtonElement) {
        var rowForToggle = weightToggle.closest('[data-cart-item]');
        if (!(rowForToggle instanceof HTMLElement)) return;
        var nextExpanded = weightToggle.getAttribute('aria-expanded') !== 'true';
        if (nextExpanded) {
          var normalizedForToggle = normalizeSelectedWeights(rowForToggle.dataset.selectedWeight || '');
          var totalWeightCellForToggle = qs('[data-row-total-weight]', rowForToggle);
          var totalWeightForToggle =
            parseWeightNumber(totalWeightCellForToggle ? totalWeightCellForToggle.textContent : '') || 0;
          renderSelectedWeightPanel(rowForToggle, normalizedForToggle, totalWeightForToggle);
        }
        setSelectedWeightPanelState(rowForToggle, nextExpanded);
        return;
      }

      if (weightHide && weightHide instanceof HTMLButtonElement) {
        var panel = weightHide.closest('[data-selected-weight-panel]');
        if (!(panel instanceof HTMLElement)) return;
        var rowForHide = panel.closest('[data-cart-item]');
        if (!(rowForHide instanceof HTMLElement)) {
          var panelRowForHide = panel.closest('[data-selected-weight-panel-row]');
          rowForHide =
            panelRowForHide && panelRowForHide.previousElementSibling instanceof HTMLElement
              ? panelRowForHide.previousElementSibling
              : null;
        }
        if (!(rowForHide instanceof HTMLElement) || !rowForHide.matches('[data-cart-item]')) return;
        setSelectedWeightPanelState(rowForHide, false, { restoreFocus: true });
        return;
      }

      if (productDetailsToggle && productDetailsToggle instanceof HTMLButtonElement) {
        if (productDetailsToggle.disabled) return;
        var productCard = productDetailsToggle.closest('[data-product-group]');
        if (!(productCard instanceof HTMLElement)) return;
        var nextExpanded = productDetailsToggle.getAttribute('aria-expanded') !== 'true';
        setProductDetailsState(productCard, nextExpanded);
        return;
      }

      if (minus || plus) {
        var wrap = t.closest('[data-cart-item]');
        if (!(wrap instanceof HTMLElement)) return;
        var input = qs('[data-quantity-input]', wrap);
        if (!(input instanceof HTMLInputElement)) return;
        var q = parseInt(input.value, 10) || 1;
        if (minus) q = Math.max(1, q - 1);
        if (plus) q = q + 1;
        input.value = String(q);
        var line = parseInt(wrap.dataset.lineNumber || '0', 10);
        if (line) changeLine(line, q, wrap.dataset.lineKey || '');
        return;
      }

      if (removeBtn) {
        var w = removeBtn.closest('[data-cart-item]');
        if (!(w instanceof HTMLElement)) return;
        if (!(removeBtn instanceof HTMLButtonElement)) return;
        var removeButton = removeBtn;
        var lineKey = String(w.dataset.lineKey || '').trim();
        if (!lineKey) return;
        if (removeButton.disabled) return;
        if (!cfg.confirmRemove) {
          removeLineByKey(lineKey, removeButton);
          return;
        }
        /** @type {HTMLElement | null} */
        var modalEl = null;
        var mid = cfg.removeConfirmModalId != null ? String(cfg.removeConfirmModalId) : '';
        if (mid) modalEl = document.getElementById(mid);
        var AC = typeof window !== 'undefined' ? window.AoneConfirmationModal : undefined;
        if (!modalEl || !AC || typeof AC.open !== 'function') {
          removeLineByKey(lineKey, removeButton);
          return;
        }
        if (removeButton.dataset.removeConfirmPending === '1') return;
        removeButton.dataset.removeConfirmPending = '1';
        AC.open(modalEl).then(function (ok) {
          if (ok) removeLineByKey(lineKey, removeButton);
        }).finally(function () {
          delete removeButton.dataset.removeConfirmPending;
        });
        return;
      }

      if (removeGroupBtn && removeGroupBtn instanceof HTMLElement) {
        var productId = String(removeGroupBtn.dataset.groupProductId || '').trim();
        if (!productId) return;
        var groupBtn = removeGroupBtn instanceof HTMLButtonElement ? removeGroupBtn : null;
        if (groupBtn && groupBtn.disabled) return;
        if (!cfg.confirmRemove) {
          removeGroup(productId, groupBtn || undefined);
          return;
        }
        var modal = null;
        var modalId = cfg.removeConfirmModalId != null ? String(cfg.removeConfirmModalId) : '';
        if (modalId) modal = document.getElementById(modalId);
        var Confirm = typeof window !== 'undefined' ? window.AoneConfirmationModal : undefined;
        if (!modal || !Confirm || typeof Confirm.open !== 'function') {
          removeGroup(productId, groupBtn || undefined);
          return;
        }
        if (groupBtn && groupBtn.dataset.removeConfirmPending === '1') return;
        if (groupBtn) groupBtn.dataset.removeConfirmPending = '1';
        Confirm.open(modal).then(function (ok) {
          if (ok) removeGroup(productId, groupBtn || undefined);
        }).finally(function () {
          if (groupBtn) delete groupBtn.dataset.removeConfirmPending;
        });
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (t instanceof HTMLInputElement && t.matches('[data-quantity-input]')) debouncedInput(t);
    });

    root.addEventListener('focusout', function (e) {
      var t = e.target;
      if (!(t instanceof HTMLInputElement) || !t.matches('[data-quantity-input]')) return;
      var rt = e.relatedTarget;
      if (rt instanceof Element && rt.closest('[data-qty-controls]')) return;
      var q = parseInt(t.value, 10);
      if (Number.isNaN(q) || q < 1) {
        t.value = '1';
        q = 1;
      }
      debouncedInput.cancel();
      var row = t.closest('[data-cart-item]');
      if (!(row instanceof HTMLElement)) return;
      var line = parseInt(row.dataset.lineNumber || '0', 10);
      if (line) changeLine(line, q, row.dataset.lineKey || '');
    });

    /** @param {Record<string, unknown>} cart @param {string} sourceId */
    function syncCartReviewFromServer(cart, sourceId) {
      if (!isAuthoritativeCart(cart) || root.__b2bCartWriteInFlight) return;
      var layout = qs('[data-b2b-cart-layout]', root);
      if (cart.items.length > 0 && !layout) {
        window.location.reload();
        return;
      }
      applyCartToDom(root, cart, cfg);
      publishCartCountUpdate(cart, sourceId);
    }

    var initialCartSeq = root.__b2bCartMutationSeq || 0;
    fetchJson(String(cfg.cartJsUrl || '/cart.js'))
      .then(function (result) {
        if (!(result && result.res && result.res.ok)) return null;
        var cart = /** @type {Record<string, unknown>} */ (result.data);
        var skuMerge = typeof window !== 'undefined' ? window.AoneCartSkuMerge : null;
        if (skuMerge && typeof skuMerge.consolidateDuplicates === 'function') {
          return skuMerge.consolidateDuplicates(cart).then(function (merged) {
            if (merged && merged.changed) {
              var flagKey = 'aoneSkuConsolidateReload';
              try {
                if (sessionStorage.getItem(flagKey) === '1') {
                  sessionStorage.removeItem(flagKey);
                  return merged.cart || cart;
                }
                sessionStorage.setItem(flagKey, '1');
              } catch (_) {
                /* ignore storage failures */
              }
              // Line keys regenerate after property merges — remount from server cart state.
              window.location.reload();
              return null;
            }
            try {
              sessionStorage.removeItem('aoneSkuConsolidateReload');
            } catch (_) {}
            return merged && merged.cart ? merged.cart : cart;
          });
        }
        return cart;
      })
      .then(function (cart) {
        if (!cart) return;
        if ((root.__b2bCartMutationSeq || 0) !== initialCartSeq || root.__b2bCartWriteInFlight) return;
        syncCartReviewFromServer(cart, 'b2b-cart-review:initial-load');
      })
      .catch(function () {
        /* keep server-rendered fallback */
      });

    document.addEventListener('cart:update', function (ev) {
      if (!(root instanceof HTMLElement) || !root.isConnected) return;
      var detail = ev && typeof ev === 'object' && 'detail' in ev ? ev.detail : null;
      if (!detail || typeof detail !== 'object') return;
      if (detail.sourceId && String(detail.sourceId).indexOf('b2b-cart-review') === 0) return;
      if (root.__b2bCartWriteInFlight) return;
      var resource = detail.resource;
      if (resource && typeof resource === 'object' && Array.isArray(resource.items)) {
        syncCartReviewFromServer(/** @type {Record<string, unknown>} */ (resource), 'b2b-cart-review:cart-update');
        return;
      }
      var externalSeq = root.__b2bCartMutationSeq || 0;
      fetchJson(String(cfg.cartJsUrl || '/cart.js'))
        .then(function (result) {
          if ((root.__b2bCartMutationSeq || 0) !== externalSeq || root.__b2bCartWriteInFlight) return;
          if (result && result.res && result.res.ok && isAuthoritativeCart(result.data)) {
            syncCartReviewFromServer(
              /** @type {Record<string, unknown>} */ (result.data),
              'b2b-cart-review:cart-update'
            );
          }
        })
        .catch(function () {
          /* ignore */
        });
    });

    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var normalized = normalizeSelectedWeights(row.dataset.selectedWeight || '');
      var totalWeightCell = qs('[data-row-total-weight]', row);
      var totalWeight = parseWeightNumber(totalWeightCell ? totalWeightCell.textContent : '') || 0;
      renderSelectedWeightPanel(row, normalized, totalWeight);
      setSelectedWeightPanelState(row, false);
    });
    syncSelectedWeightPanelPlacement(root);
    var weightLayoutMq = window.matchMedia('(max-width: 767px)');
    var onWeightLayoutChange = function () {
      if (!(root instanceof HTMLElement) || !root.isConnected) return;
      syncSelectedWeightPanelPlacement(root);
    };
    if (typeof weightLayoutMq.addEventListener === 'function') {
      weightLayoutMq.addEventListener('change', onWeightLayoutChange);
    } else if (typeof weightLayoutMq.addListener === 'function') {
      weightLayoutMq.addListener(onWeightLayoutChange);
    }
    reconcileProductDetails(root);

    var goldPricing = getGoldPricing();
    if (goldPricing) {
      goldPricing.initFromDom(document);
      var refreshPricing = function () {
        recalculateCartPricing(root, cfg, root.__b2bLastCartItems || []);
      };
      goldPricing.waitForReady().then(refreshPricing);
      document.addEventListener(goldPricing.UPDATE_EVENT || 'aone:gold-prices-updated', refreshPricing);
    } else {
      recalculateCartPricing(root, cfg);
    }

    scheduleLiveLabourRefresh(root, cfg, 'initial-load');
    startLiveLabourPolling(root, cfg);

    var onVisibilityOrFocus = function () {
      if (!(root instanceof HTMLElement) || !root.isConnected) return;
      if (document.hidden) {
        stopLiveLabourPolling(root);
        return;
      }
      startLiveLabourPolling(root, cfg);
      scheduleLiveLabourRefresh(root, cfg, 'visibility-focus');
    };
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);
    window.addEventListener('pageshow', onVisibilityOrFocus);

    var sectionEl = root.closest('.shopify-section');
    if (sectionEl instanceof HTMLElement) {
      sectionEl.addEventListener('shopify:section:unload', function () {
        stopActiveCartSyncPoll();
        stopLiveLabourPolling(root);
        document.removeEventListener('visibilitychange', onVisibilityOrFocus);
        window.removeEventListener('focus', onVisibilityOrFocus);
        window.removeEventListener('pageshow', onVisibilityOrFocus);
        if (root.__b2bScheduleLiveLabour && typeof root.__b2bScheduleLiveLabour.cancel === 'function') {
          root.__b2bScheduleLiveLabour.cancel();
        }
      });
    }

    if (!(syncBtn instanceof HTMLButtonElement)) return;
    var syncInProgress = false;

    /**
     * @param {Record<string, unknown>} cfg
     * @param {string} syncId
     */
    function getSyncStatusPath(cfg, syncId) {
      var explicit =
        cfg.syncStatusPath != null ? String(cfg.syncStatusPath).trim() : '';
      if (explicit) {
        explicit = normalizeAppProxyPath(explicit, '/apps/aone-api/cart-sync/:syncId/status');
        if (explicit.indexOf(':syncId') >= 0) {
          return explicit.replace(':syncId', encodeURIComponent(syncId));
        }
        if (explicit.slice(-1) === '/') {
          return explicit + encodeURIComponent(syncId);
        }
        return explicit;
      }
      var syncPath = normalizeAppProxyPath(cfg.syncPath, '/apps/aone-api/cart-sync');
      return syncPath + '/' + encodeURIComponent(syncId) + '/status';
    }

    /**
     * Poll cart-sync job status until a terminal backend state is reached.
     * Uses sequential await (no overlapping requests) and cache-bypassing GETs
     * so stale PROCESSING responses cannot keep polling alive after completion.
     *
     * @param {Record<string, unknown>} cfg
     * @param {string} syncId
     * @param {string} syncRequestId
     * @param {AbortSignal|null} [pollSignal]
     */
    async function pollThemeSyncStatus(cfg, syncId, syncRequestId, pollSignal) {
      var statusUrl = getSyncStatusPath(cfg, syncId);
      var pollSalesRepEmail = getSalesRepEmail(cfg);
      if (pollSalesRepEmail) {
        statusUrl +=
          (statusUrl.indexOf('?') >= 0 ? '&' : '?') +
          'salesRepEmail=' +
          encodeURIComponent(pollSalesRepEmail);
      }
      var pollIntervalMsRaw = Number(cfg.syncPollIntervalMs);
      var pollIntervalMs =
        Number.isFinite(pollIntervalMsRaw) && pollIntervalMsRaw >= 500 ? pollIntervalMsRaw : 1500;
      var pollTimeoutMsRaw = Number(cfg.syncPollTimeoutMs);
      var pollTimeoutMs =
        Number.isFinite(pollTimeoutMsRaw) && pollTimeoutMsRaw >= 10000 ? pollTimeoutMsRaw : 10 * 60 * 1000;
      var startedAt = Date.now();
      var networkFailures = 0;
      var attempt = 0;
      var terminalHandled = false;

      logSyncOrderProxy('cart-sync status polling started', { url: statusUrl, syncId: syncId });

      while (true) {
        if (pollSignal && pollSignal.aborted) {
          logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'aborted' });
          return {
            ok: false,
            data: {
              errorCode: 'SYNC_CANCELLED',
              message: String(cfg.syncErrorFallback || 'Unable to sync order'),
              syncId: syncId,
              syncRequestId: syncRequestId,
            },
          };
        }

        attempt += 1;
        /** @type {RequestInit} */
        var pollFetchOpts = { method: 'GET', cache: 'no-store' };
        if (pollSignal) pollFetchOpts.signal = pollSignal;

        /** @type {{ res: Response, data: Record<string, unknown> }|null} */
        var result = null;
        try {
          result = await fetchApiJson(statusUrl, pollFetchOpts);
        } catch (err) {
          if (pollSignal && pollSignal.aborted) {
            logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'aborted' });
            return {
              ok: false,
              data: {
                errorCode: 'SYNC_CANCELLED',
                message: String(cfg.syncErrorFallback || 'Unable to sync order'),
                syncId: syncId,
                syncRequestId: syncRequestId,
              },
            };
          }
          networkFailures += 1;
          logCartSyncDebug('status poll network error', {
            syncId: syncId,
            attempt: attempt,
            networkFailures: networkFailures,
            error: err && err.message ? String(err.message) : 'fetch failed',
          });
          if (networkFailures >= 3) {
            return {
              ok: false,
              data: {
                errorCode: 'SYNC_STATUS_UNAVAILABLE',
                message: String(cfg.syncErrorFallback || 'Unable to sync order'),
                syncId: syncId,
                syncRequestId: syncRequestId,
              },
            };
          }
          await waitMs(Math.min(pollIntervalMs * networkFailures, 5000));
          continue;
        }

        if (!result.res.ok || result.res.redirected) {
          logSyncOrderProxy('cart-sync status response', {
            status: result.res.status,
            redirected: result.res.redirected,
            finalUrl: result.res.url,
            syncId: syncId,
            attempt: attempt,
          });
        }

        if (!result.res.ok) {
          networkFailures += 1;
          if (networkFailures >= 3) {
            return {
              ok: false,
              data: result.data || {},
            };
          }
          await waitMs(Math.min(pollIntervalMs * networkFailures, 5000));
          continue;
        }

        networkFailures = 0;
        var responseData = unwrapApiData(result.data);
        var status = readCartSyncStatus(responseData);
        var rawStatus = responseData.status != null ? responseData.status : responseData.syncStatus;

        logCartSyncDebug('status poll', {
          syncId: syncId,
          attempt: attempt,
          status: status,
          rawStatus: rawStatus,
        });

        if (SUCCESS_CART_SYNC_STATUSES[status]) {
          if (terminalHandled) {
            logCartSyncDebug('ignored duplicate terminal success', { syncId: syncId, status: status });
            return {
              ok: true,
              data:
                responseData.result && typeof responseData.result === 'object'
                  ? /** @type {Record<string, unknown>} */ (responseData.result)
                  : responseData,
            };
          }
          terminalHandled = true;
          logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'completed', status: status });
          return {
            ok: true,
            data:
              responseData.result && typeof responseData.result === 'object'
                ? /** @type {Record<string, unknown>} */ (responseData.result)
                : responseData,
          };
        }

        if (FAILURE_CART_SYNC_STATUSES[status]) {
          if (terminalHandled) {
            logCartSyncDebug('ignored duplicate terminal failure', { syncId: syncId, status: status });
            return {
              ok: false,
              data: {
                errorCode: responseData.errorCode || 'CART_VALIDATION_FAILED',
                message:
                  responseData.message != null
                    ? String(responseData.message)
                    : String(cfg.syncErrorFallback || 'Unable to sync order'),
                details: Array.isArray(responseData.details) ? responseData.details : [],
                syncId: syncId,
                syncRequestId: syncRequestId,
              },
            };
          }
          terminalHandled = true;
          logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'failed', status: status });
          return {
            ok: false,
            data: {
              errorCode: responseData.errorCode || 'CART_VALIDATION_FAILED',
              message:
                responseData.message != null
                  ? String(responseData.message)
                  : String(cfg.syncErrorFallback || 'Unable to sync order'),
              details: Array.isArray(responseData.details) ? responseData.details : [],
              syncId: syncId,
              syncRequestId: syncRequestId,
            },
          };
        }

        if (!ACTIVE_CART_SYNC_STATUSES[status]) {
          terminalHandled = true;
          logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'unknown-status', status: status });
          return {
            ok: false,
            data: {
              errorCode: 'SYNC_STATUS_UNKNOWN',
              message: String(cfg.syncErrorFallback || 'Unable to sync order'),
              syncId: syncId,
              syncRequestId: syncRequestId,
            },
          };
        }

        if (Date.now() - startedAt >= pollTimeoutMs) {
          terminalHandled = true;
          logCartSyncDebug('polling stopped', { syncId: syncId, reason: 'timeout', status: status });
          return {
            ok: false,
            data: {
              errorCode: 'SYNC_TIMEOUT',
              message: 'Sync is taking longer than expected. Please refresh the status or try again.',
              syncId: syncId,
              syncRequestId: syncRequestId,
            },
          };
        }

        await waitMs(pollIntervalMs);
      }
    }

    /** @param {{id:number|string,name:string,customerNumber?:string}} customer */
    function resolveSyncCustomerNumber(customer) {
      if (!customer || typeof customer !== 'object') return '';
      var raw = /** @type {Record<string, unknown>} */ (customer);
      var values = [
        raw.customerNumber,
        raw.customer_number,
        raw.customerNo,
        raw.customer_no,
        raw.customerCode,
        raw.customer_code,
        raw.code,
      ];
      for (var i = 0; i < values.length; i++) {
        if (values[i] == null) continue;
        var out = String(values[i]).trim();
        if (out) return out;
      }
      return '';
    }

    /** @param {Record<string, unknown>} data */
    function extractSyncedCartLabel(data) {
      if (!data || typeof data !== 'object') return '';
      var row = /** @type {Record<string, unknown>} */ (data);
      var uidRaw =
        row.cartUid != null
          ? row.cartUid
          : row.cart_uid != null
            ? row.cart_uid
            : row.externalCartRef != null
              ? row.externalCartRef
              : row.external_cart_ref != null
                ? row.external_cart_ref
                : row.uid != null
                  ? row.uid
                  : null;
      if (uidRaw == null || String(uidRaw).trim() === '') return '';
      var uid = String(uidRaw).trim();
      if (/^CART-/i.test(uid)) return uid.toUpperCase();
      var stripped = uid.replace(/^0+/, '');
      if (!stripped) stripped = uid;
      return 'CART-' + stripped;
    }

    /** @param {Record<string, unknown>} cfg @param {Record<string, unknown>} resultData */
    function formatSyncSuccessMessage(cfg, resultData) {
      var cartLabel = extractSyncedCartLabel(resultData);
      if (cartLabel) return 'Order synced successfully to ' + cartLabel + '.';
      return String(cfg.syncSuccessMessage || 'Order synced successfully');
    }

    /** @param {Record<string, unknown>} errData @param {{id:number|string,name:string,customerNumber?:string}} customer @param {Record<string, unknown>} cart @returns {Promise<boolean>} */
    async function handleCartDestinationSyncError(errData, customer, cart) {
      var errCode =
        errData.errorCode != null
          ? String(errData.errorCode)
          : errData.code != null
            ? String(errData.code)
            : '';
      if (errCode === 'CART_EXPIRED' || errCode === 'CART_NOT_EDITABLE') {
        dismissSyncModal(true);
        showToast(cfg, formatSyncErrorMessage(errData, cfg), 'error');
        var retryDestination = await openCartDestinationPicker(customer, {
          bannerMessage:
            errCode === 'CART_NOT_EDITABLE'
              ? 'This cart can no longer be edited. Please choose another cart.'
              : 'This cart is no longer active. Please choose another cart.',
        });
        if (retryDestination) {
          await performSync(cart, customer, retryDestination);
        }
        return true;
      }
      return false;
    }

    /** @param {{id:number|string,name:string,customerNumber?:string}} customer @param {{bannerMessage?: string}} [extra] */
    async function openCartDestinationPicker(customer, extra) {
      extra = extra || {};
      var CartSelector =
        typeof window !== 'undefined' ? window.AonegndSyncCartSelector : undefined;
      if (!CartSelector || typeof CartSelector.open !== 'function') {
        showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
        return null;
      }
      return CartSelector.open({
        cartsPath: cfg.cartsPath,
        salesRepEmail: getSalesRepEmail(cfg),
        currencyCode: cfg.currencyCode,
        customer: customer,
        cancelLabel: cfg.cartSelectorCancelLabel,
        syncOrderLabel: cfg.cartSelectorSyncLabel,
        bannerMessage: extra.bannerMessage,
      });
    }

    /** @param {{id:number|string,name:string,customerNumber?:string}} customer */
    function syncCustomerDisplayName(customer) {
      var name = customer && customer.name != null ? String(customer.name).trim() : '';
      var id = customer && customer.id != null ? String(customer.id).trim() : '';
      if (!name || (id && name === id)) return '';
      return name;
    }

    /** @param {Record<string, unknown>} resultData @param {{cartLabel?: string}|null} destination */
    function confirmedCartLabel(resultData, destination) {
      var fromResult = extractSyncedCartLabel(resultData);
      if (fromResult) return fromResult;
      var nested =
        resultData && resultData.result && typeof resultData.result === 'object'
          ? /** @type {Record<string, unknown>} */ (resultData.result)
          : null;
      if (nested) {
        fromResult = extractSyncedCartLabel(nested);
        if (fromResult) return fromResult;
      }
      var fromDestination = destination && destination.cartLabel != null ? String(destination.cartLabel).trim() : '';
      return fromDestination;
    }

    var syncModalGeneration = 0;
    var UNRESOLVED_SYNC_CODES = { SYNC_TIMEOUT: true, SYNC_STATUS_UNAVAILABLE: true, SYNC_STATUS_UNKNOWN: true };

    /** @param {HTMLElement} el @param {boolean} hidden */
    function setSyncModalHidden(el, hidden) {
      if (!(el instanceof HTMLElement)) return;
      if (hidden) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    /** @param {string} label */
    function visibleSyncCartId(label) {
      var text = String(label || '').replace(/^CART-/i, '').trim();
      if (!text || text === 'undefined' || text === 'null' || text === '0' || text === '-' || text === '\u2014') return '';
      return text;
    }

    function syncModalElement() {
      var section = root.closest('.shopify-section') || document;
      var modal = section.querySelector('[data-aone-sync-success]');
      return modal instanceof HTMLElement ? modal : null;
    }

    /** @param {boolean} [force] */
    function dismissSyncModal(force) {
      var modal = syncModalElement();
      if (modal && typeof modal.__closeSyncSuccess === 'function') modal.__closeSyncSuccess(!!force);
    }

    /** @param {HTMLElement} modal */
    function bindSyncSuccessPopup(modal) {
      if (modal.getAttribute('data-aone-sync-success-ready') === 'true') return;
      modal.setAttribute('data-aone-sync-success-ready', 'true');
      var dialog = modal.querySelector('.aone-sync-success__dialog');
      var focusable = 'button:not([disabled]):not([hidden]), [href], [tabindex]:not([tabindex="-1"])';

      function closePopup(force) {
        if (!force && modal.getAttribute('data-state') === 'loading') return;
        if (modal.hasAttribute('hidden')) return;
        if (modal.__statusCheck) stopActiveCartSyncPoll();
        modal.setAttribute('data-state', 'idle');
        modal.setAttribute('hidden', '');
        modal.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', onKeydown, true);
        document.documentElement.style.overflow = modal.dataset.prevHtmlOverflow || '';
        document.body.style.overflow = modal.dataset.prevBodyOverflow || '';
        var returnFocus = modal.__returnFocus;
        modal.__returnFocus = null;
        if (returnFocus && typeof returnFocus.focus === 'function') {
          try {
            returnFocus.focus();
          } catch (err) {
            /* The previous control may already be gone. */
          }
        }
      }

      function onKeydown(event) {
        if (modal.hasAttribute('hidden')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closePopup(false);
          return;
        }
        if (event.key !== 'Tab' || !(dialog instanceof HTMLElement)) return;
        var items = dialog.querySelectorAll(focusable);
        if (!items.length) {
          event.preventDefault();
          return;
        }
        var first = items[0];
        var last = items[items.length - 1];
        var active = document.activeElement;
        if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || active === dialog || !dialog.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }

      document.addEventListener('shopify:section:unload', function (event) {
        var target = event.target;
        if (target instanceof HTMLElement && target.contains(modal)) {
          stopActiveCartSyncPoll();
          closePopup(true);
        }
      });

      modal.addEventListener('click', function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('[data-aone-sync-success-dismiss]')) {
          closePopup(false);
          return;
        }
        if (target.closest('[data-aone-sync-success-done]')) {
          closePopup(false);
          return;
        }
        if (target.closest('[data-aone-sync-success-view]')) {
          var label = modal.dataset.cartLabel || '';
          closePopup(false);
          if (window.AoneYourCarts && typeof window.AoneYourCarts.focusCart === 'function' && label) {
            window.AoneYourCarts.focusCart(label);
            return;
          }
          var table = document.querySelector('[data-aone-your-carts]');
          if (table instanceof HTMLElement) table.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (target.closest('[data-aone-sync-retry]')) {
          retrySyncFromModal(modal);
          return;
        }
        if (target.closest('[data-aone-sync-check]')) {
          recheckSyncFromModal(modal);
        }
      });

      modal.__closeSyncSuccess = closePopup;
      modal.__onSyncSuccessKeydown = onKeydown;
    }

    /**
     * @param {'loading'|'success'|'error'|'reconciling'} state
     * @param {{ customer?: {id:number|string,name:string,customerNumber?:string}, cartLabel?: string, message?: string, canRepost?: boolean, syncId?: string, attempt?: {cart: Record<string, unknown>, customer: {id:number|string,name:string,customerNumber?:string}, destination: {cartLabel?: string}} }} [details]
     * @returns {HTMLElement|null}
     */
    function renderSyncModal(state, details) {
      details = details || {};
      var modal = syncModalElement();
      if (!(modal instanceof HTMLElement)) return null;
      if (state === 'success' && modal.getAttribute('data-state') === 'success' && !modal.hasAttribute('hidden')) {
        return modal;
      }
      bindSyncSuccessPopup(modal);
      var wasOpen = !modal.hasAttribute('hidden');
      if (details.customer) {
        var nameEl = modal.querySelector('[data-aone-sync-success-customer]');
        var numberEl = modal.querySelector('[data-aone-sync-success-customer-no]');
        var customerName = syncCustomerDisplayName(details.customer);
        var customerNumber = resolveSyncCustomerNumber(details.customer);
        if (nameEl instanceof HTMLElement) {
          nameEl.textContent = customerName;
          setSyncModalHidden(nameEl, !customerName);
        }
        if (numberEl instanceof HTMLElement) {
          numberEl.textContent = customerNumber ? 'Customer No: ' + customerNumber : '';
          setSyncModalHidden(numberEl, !customerNumber);
        }
      }
      if (details.cartLabel != null) modal.dataset.cartLabel = String(details.cartLabel);
      if (details.cartFlow === 'existing' || details.cartFlow === 'new') modal.dataset.cartFlow = details.cartFlow;
      if (details.syncId != null) {
        if (details.syncId) modal.dataset.syncId = String(details.syncId);
        else delete modal.dataset.syncId;
      }
      if (details.attempt) modal.__syncAttempt = details.attempt;
      modal.dataset.canRepost = details.canRepost ? 'true' : 'false';

      var copy = {
        loading: {
          title: 'Syncing Order...',
          message: 'Please wait while we create the order and send it for review.',
          badge: 'Processing',
        },
        success: {
          title: 'Sync Order Successful',
          message: 'Cart has been sent for review successfully.',
          badge: 'Sent for Review',
        },
        error: {
          title: 'Unable to Sync Order',
          message: details.message || "We couldn't complete the order synchronization. Please try again.",
          badge: 'Failed',
        },
        reconciling: {
          title: 'Still Confirming Sync',
          message: details.message || 'The sync has not reached a final result yet. Check the existing request before sending it again.',
          badge: 'Confirming',
        },
      };
      var nextCopy = copy[state] || copy.loading;
      var titleEl = modal.querySelector('[data-aone-sync-success-title]');
      var messageEl = modal.querySelector('[data-aone-sync-success-message]');
      var badgeEl = modal.querySelector('[data-aone-sync-success-badge]');
      if (titleEl) titleEl.textContent = nextCopy.title;
      if (messageEl) messageEl.textContent = nextCopy.message;
      if (badgeEl) badgeEl.textContent = nextCopy.badge;
      var cartLine = modal.querySelector('[data-aone-sync-success-cart-line]');
      var successCartId = visibleSyncCartId(modal.dataset.cartLabel || '');
      var showCartLine = !!successCartId && (
        state === 'success' ||
        state === 'error' ||
        state === 'reconciling' ||
        (state === 'loading' && modal.dataset.cartFlow === 'existing')
      );
      if (cartLine instanceof HTMLElement) {
        cartLine.textContent = showCartLine ? 'Cart ID: ' + successCartId : '';
        setSyncModalHidden(cartLine, !showCartLine);
      }
      if (titleEl instanceof HTMLElement) setSyncModalHidden(titleEl, state === 'success');

      var closeBtn = modal.querySelector('.aone-sync-success__close');
      var waitBtn = modal.querySelector('[data-aone-sync-wait]');
      var viewBtn = modal.querySelector('[data-aone-sync-success-view]');
      var doneBtn = modal.querySelector('[data-aone-sync-success-done]');
      var checkBtn = modal.querySelector('[data-aone-sync-check]');
      var retryBtn = modal.querySelector('[data-aone-sync-retry]');
      var errorClose = modal.querySelector('.aone-sync-success__actions [data-aone-sync-success-dismiss]');
      setSyncModalHidden(closeBtn, state === 'loading');
      setSyncModalHidden(waitBtn, state !== 'loading');
      setSyncModalHidden(viewBtn, state !== 'success');
      setSyncModalHidden(doneBtn, state !== 'success');
      setSyncModalHidden(checkBtn, state !== 'reconciling' || !modal.dataset.syncId);
      setSyncModalHidden(retryBtn, state !== 'error' || modal.dataset.canRepost !== 'true');
      setSyncModalHidden(errorClose, state !== 'error' && state !== 'reconciling');
      if (checkBtn instanceof HTMLButtonElement) checkBtn.disabled = false;

      modal.setAttribute('data-state', state);
      var dialog = modal.querySelector('.aone-sync-success__dialog');
      if (dialog instanceof HTMLElement) {
        dialog.setAttribute('aria-busy', state === 'loading' || state === 'reconciling' ? 'true' : 'false');
        dialog.setAttribute('aria-labelledby', state === 'success' ? 'aone-sync-success-message' : 'aone-sync-success-title');
      }
      var stage = modal.querySelector('.aone-sync-success__stage');
      if (wasOpen && stage instanceof HTMLElement) {
        stage.classList.remove('is-switching');
        void stage.offsetWidth;
        stage.classList.add('is-switching');
      }
      if (!wasOpen) {
        modal.__returnFocus = document.activeElement;
        modal.dataset.prevHtmlOverflow = document.documentElement.style.overflow;
        modal.dataset.prevBodyOverflow = document.body.style.overflow;
        modal.removeAttribute('hidden');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        if (modal.__onSyncSuccessKeydown) {
          document.removeEventListener('keydown', modal.__onSyncSuccessKeydown, true);
          document.addEventListener('keydown', modal.__onSyncSuccessKeydown, true);
        }
        if (dialog instanceof HTMLElement) dialog.focus();
      }
      return modal;
    }

    /** @param {HTMLElement} modal */
    function retrySyncFromModal(modal) {
      if (syncInProgress || modal.dataset.canRepost !== 'true' || modal.getAttribute('data-state') !== 'error') return;
      var attempt = modal.__syncAttempt;
      if (!attempt || !attempt.cart || !attempt.customer || !attempt.destination) return;
      runSyncAttempt(attempt.cart, attempt.customer, attempt.destination);
    }

    /** @param {HTMLElement} modal */
    async function recheckSyncFromModal(modal) {
      if (modal.__statusCheck || modal.getAttribute('data-state') !== 'reconciling') return;
      var syncId = modal.dataset.syncId || '';
      if (!syncId) return;
      var op = Number(modal.dataset.op || '0');
      modal.__statusCheck = true;
      var checkBtn = modal.querySelector('[data-aone-sync-check]');
      if (checkBtn instanceof HTMLButtonElement) {
        checkBtn.disabled = true;
        checkBtn.textContent = 'Checking...';
      }
      var pollAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      stopActiveCartSyncPoll();
      activeCartSyncPollAbort = pollAbortController;
      var outcome = await pollThemeSyncStatus(cfg, syncId, '', pollAbortController ? pollAbortController.signal : null);
      modal.__statusCheck = false;
      if (checkBtn instanceof HTMLButtonElement) {
        checkBtn.disabled = false;
        checkBtn.textContent = 'Check status';
      }
      if (activeCartSyncPollAbort === pollAbortController) activeCartSyncPollAbort = null;
      if (Number(modal.dataset.op || '0') !== op || modal.hasAttribute('hidden')) return;
      var data = /** @type {Record<string, unknown>} */ (outcome.data || {});
      if (outcome.ok) {
        var cartLabel = confirmedCartLabel(data, null) || modal.dataset.cartLabel || '';
        renderSyncModal('success', { cartLabel: cartLabel });
        refreshCartsAfterSync(cartLabel);
        clearShopifyCartAfterSync();
        return;
      }
      var code = data.errorCode != null ? String(data.errorCode) : '';
      if (code === 'SYNC_CANCELLED') return;
      if (UNRESOLVED_SYNC_CODES[code]) {
        renderSyncModal('reconciling', {
          syncId: syncId,
          canRepost: false,
          message: 'The sync has not reached a final result yet. Check the existing request before sending it again.',
        });
        return;
      }
      renderSyncModal('error', {
        canRepost: true,
        message: formatSyncErrorMessage(data, cfg),
      });
    }

    /** @param {string} cartLabel */
    function refreshCartsAfterSync(cartLabel) {
      try {
        if (window.AoneYourCarts && typeof window.AoneYourCarts.refresh === 'function') {
          window.AoneYourCarts.refresh(cartLabel);
        }
      } catch (refreshError) {
        /* A table refresh problem must not turn a confirmed sync into a failure. */
      }
    }

    async function clearShopifyCartAfterSync() {
      if (!cfg.clearCartAfterSync) return;
      var clearResult = await fetchJson(String(cfg.cartClearJsUrl || '/cart/clear.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      var clearedCart = /** @type {Record<string, unknown>} */ (clearResult.data);
      if (clearResult.res.ok && !clearedCart.errors) {
        if (typeof clearedCart.item_count !== 'number') clearedCart.item_count = 0;
        if (!Array.isArray(clearedCart.items)) clearedCart.items = [];
        applyCartToDom(root, clearedCart, cfg);
        publishCartCountUpdate(clearedCart, 'b2b-cart-review:sync-clear');
      }
    }

    /**
     * @param {Record<string, unknown>} cart
     * @param {{id:number|string,name:string,customerNumber?:string}} customer
     * @param {{type:'NEW_CART', cartLabel?: string}|{type:'EXISTING_CART', cartId:number, cartLabel?: string}} destination
     */
    function runSyncAttempt(cart, customer, destination) {
      if (syncInProgress) return;
      syncInProgress = true;
      var labelEl = qs('[data-sync-button-label]', root);
      var defaultLabel = cfg.syncButtonTextDefault != null ? String(cfg.syncButtonTextDefault) : 'SEND FOR REVIEW';
      syncBtn.disabled = true;
      syncBtn.classList.add('is-loading');
      if (labelEl) labelEl.textContent = String(cfg.syncLoadingText || 'SYNCING...');
      performSync(cart, customer, destination).finally(function () {
        syncInProgress = false;
        syncBtn.disabled = false;
        syncBtn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = defaultLabel;
        updateSalesRepGate(root, cfg);
      });
    }

    /** @param {Record<string, unknown>} cart @param {{id:number|string,name:string,customerNumber?:string}} customer @param {{type:'NEW_CART', cartLabel?: string}|{type:'EXISTING_CART', cartId:number, cartLabel?: string}} destination */
    async function performSync(cart, customer, destination) {
      stopActiveCartSyncPoll();
      var pollAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
      activeCartSyncPollAbort = pollAbortController;
      var syncOutcomeHandled = false;
      var modalOp = 0;
      var acceptedSyncId = '';

      /**
       * @param {string} message
       * @param {'success'|'error'} type
       * @param {{ unresolved?: boolean, canRepost?: boolean, syncId?: string }} [extra]
       */
      function reportSyncOutcome(message, type, extra) {
        if (syncOutcomeHandled) return;
        if (modalOp && modalOp !== syncModalGeneration) return;
        syncOutcomeHandled = true;
        extra = extra || {};
        var cartLabel = confirmedCartLabel(completedResult, destination) || (destination && destination.cartLabel ? String(destination.cartLabel) : '');
        if (type === 'success') {
          var successModal = renderSyncModal('success', { customer: customer, cartLabel: cartLabel });
          if (!successModal) showToast(cfg, message, 'success');
          refreshCartsAfterSync(cartLabel);
          return;
        }
        if (extra.unresolved) {
          var pendingModal = renderSyncModal('reconciling', {
            customer: customer,
            cartLabel: cartLabel,
            syncId: extra.syncId || '',
            canRepost: false,
            message: extra.syncId
              ? 'The sync has not reached a final result yet. Check the existing request before sending it again.'
              : "We couldn't confirm whether the sync started. Check Your Carts before sending the order again.",
          });
          if (!pendingModal) showToast(cfg, message, 'error');
          return;
        }
        var generic = String(cfg.syncErrorFallback || 'Unable to sync order');
        var errorMessage = message && message !== generic
          ? message
          : "We couldn't complete the order synchronization. Please try again.";
        var errorModal = renderSyncModal('error', {
          customer: customer,
          cartLabel: cartLabel,
          canRepost: extra.canRepost === true,
          message: errorMessage,
        });
        if (!errorModal) showToast(cfg, message, 'error');
      }

      try {
      var pricingError = validateCartPricing(cart, cfg, root);
      if (pricingError) {
        showToast(cfg, pricingError, 'error');
        return;
      }

      var salesRepEmail = getSalesRepEmail(cfg);
      if (!salesRepEmail) {
        showToast(cfg, 'Please log in as a sales representative to sync this order.', 'error');
        return;
      }

      var customerId = parsePositiveIntegerId(
        customer && customer.id != null
          ? customer.id
          : customer && customer.customerId != null
            ? customer.customerId
            : null
      );
      if (customerId == null) {
        showToast(cfg, 'Please select a valid customer before syncing this order.', 'error');
        return;
      }

      if (!destination || typeof destination !== 'object' || !destination.type) {
        showToast(cfg, 'Please select a cart destination before syncing this order.', 'error');
        return;
      }

      if (destination.type === 'EXISTING_CART') {
        var destinationCartId = parsePositiveIntegerId(destination.cartId);
        if (destinationCartId == null) {
          showToast(cfg, 'Please select a valid active cart before syncing this order.', 'error');
          return;
        }
      } else if (destination.type !== 'NEW_CART') {
        showToast(cfg, 'Please select a valid cart destination before syncing this order.', 'error');
        return;
      }

      var syncUrl = normalizeAppProxyPath(cfg.syncPath, '/apps/aone-api/cart-sync');
      syncUrl +=
        (syncUrl.indexOf('?') >= 0 ? '&' : '?') +
        'salesRepEmail=' +
        encodeURIComponent(salesRepEmail);
      var items = Array.isArray(cart.items) ? cart.items : [];
      var goldPricing = getGoldPricing();
      seedTariffPercentageMapFromDom(root);
      var syncRequestId = createSyncRequestId();
      var customerNumber = resolveSyncCustomerNumber(customer);
      var body = {
        salesRepEmail: salesRepEmail,
        customerId: customerId,
        customerNumber: customerNumber || null,
        syncRequestId: syncRequestId,
        shopifyCartToken: cart.token != null ? String(cart.token) : null,
        // TODO: Backend must recalculate totals from captured gold rate, selected weight, material, and labour - do not trust browser totals alone.
        computed_total_cents:
          typeof root.__b2bComputedOrderTotalCents === 'number' ? root.__b2bComputedOrderTotalCents : null,
        destination:
          destination.type === 'EXISTING_CART'
            ? { type: 'EXISTING_CART', cartId: parsePositiveIntegerId(destination.cartId) }
            : { type: 'NEW_CART' },
        items: items.map(function (raw) {
          var item = /** @type {Record<string, unknown>} */ (raw);
          var props =
            item.properties && typeof item.properties === 'object'
              ? /** @type {Record<string, unknown>} */ (item.properties)
              : {};
          var material = jewelleryMaterialFromItem(item);
          var weights = weightStatsFromItem(item);
          var labour = labourChargeForRow(item);
          var weightText = props.Weight != null ? String(props.Weight).trim() : '';
          /** @type {Record<string, unknown>} */
          var payloadItem = {
            lineKey: item.key != null ? String(item.key) : undefined,
            productId: item.product_id != null ? item.product_id : undefined,
            variantId: item.variant_id,
            quantity: item.quantity,
            sku: item.sku != null ? String(item.sku) : props.SKU != null ? String(props.SKU) : '',
            weight: weightText,
            weightUnit: 'g',
            properties: props,
            captured_gold_price_per_troy_oz:
              props._gold_price_per_troy_oz != null ? String(props._gold_price_per_troy_oz) : '',
            captured_gold_price_material:
              props._gold_price_material != null ? String(props._gold_price_material) : material,
            captured_gold_price_captured_at:
              props._gold_price_captured_at != null ? String(props._gold_price_captured_at) : '',
            selected_weight_total_grams: roundMoney2(weights.total),
            labour_charge_cents: labour.hasValue ? labour.cents : 0,
            labour_per_gram: labour.hasValue ? roundMoney2(labour.cents / 100) : 0,
            gold_karat: goldPricing && goldPricing.normalizeGoldKarat ? goldPricing.normalizeGoldKarat(material) : material,
          };
          if (goldPricing) {
            var pricingResult = goldPricing.calculateRowPricing({
              properties: props,
              rowMaterial: material,
              totalWeightGrams: weights.total,
              labourCents: labour.hasValue ? labour.cents : 0,
              labourResolved: labour.hasValue,
              cartCurrency: String(cfg.currencyCode || goldPricing.getActiveCurrencyCode()),
              preferLive: true,
              allowFallback: false,
              tariffPercentage: tariffPercentageForRow(item),
            });
            payloadItem.computed_row_total_cents =
              pricingResult.status === 'ok' && pricingResult.rowTotalCents != null
                ? pricingResult.rowTotalCents
                : null;
            payloadItem.live_gold_price_per_gram =
              pricingResult.status === 'ok' && pricingResult.goldPricePerGram != null
                ? pricingResult.goldPricePerGram
                : null;
            payloadItem.unit_price_per_gram =
              pricingResult.status === 'ok' && pricingResult.unitPricePerGram != null
                ? pricingResult.unitPricePerGram
                : null;
            payloadItem.gold_rate_updated_at =
              pricingResult.goldRateUpdatedAt != null ? String(pricingResult.goldRateUpdatedAt) : '';
            if (pricingResult.totalWeightGrams != null) {
              payloadItem.selected_weight_total_grams = pricingResult.totalWeightGrams;
            }
            if (pricingResult.labourPerGram != null) {
              payloadItem.labour_per_gram = pricingResult.labourPerGram;
            }
          }
          return payloadItem;
        }),
      };

        logSyncOrderProxy('cart-sync request', { url: syncUrl, customerId: customerId });
        modalOp = syncModalGeneration + 1;
        syncModalGeneration = modalOp;
        renderSyncModal('loading', {
          customer: customer,
          cartFlow: destination.type === 'EXISTING_CART' ? 'existing' : 'new',
          cartLabel: destination.type === 'EXISTING_CART' && destination.cartLabel ? String(destination.cartLabel) : '',
          syncId: '',
          canRepost: false,
          attempt: { cart: cart, customer: customer, destination: destination },
        });
        var openedModal = syncModalElement();
        if (openedModal) openedModal.dataset.op = String(modalOp);

        var result = await fetchApiJson(syncUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        logSyncOrderProxy('cart-sync response', {
          status: result.res.status,
          redirected: result.res.redirected,
          finalUrl: result.res.url,
        });

        /** @type {Record<string, unknown>} */
        var completedResult = {};
        if (result.res.status === 202) {
          /** @type {Record<string, unknown>} */
          var accepted = unwrapApiData(result.data);
          var syncId =
            accepted.syncId != null
              ? String(accepted.syncId)
              : accepted.id != null
                ? String(accepted.id)
                : '';
          if (!syncId) {
            reportSyncOutcome(String(cfg.syncErrorFallback || 'Unable to sync order'), 'error', { unresolved: true });
            return;
          }
          acceptedSyncId = syncId;
          var pollingModal = syncModalElement();
          if (pollingModal) pollingModal.dataset.syncId = syncId;
          var pollOutcome = await pollThemeSyncStatus(
            cfg,
            syncId,
            accepted.syncRequestId != null ? String(accepted.syncRequestId) : syncRequestId,
            pollAbortController ? pollAbortController.signal : null
          );
          if (!pollOutcome.ok) {
            var pollErr = /** @type {Record<string, unknown>} */ (pollOutcome.data || {});
            if (pollErr.errorCode === 'SYNC_CANCELLED') {
              return;
            }
            if (UNRESOLVED_SYNC_CODES[String(pollErr.errorCode || '')]) {
              reportSyncOutcome(formatSyncErrorMessage(pollErr, cfg), 'error', {
                unresolved: true,
                syncId: acceptedSyncId,
              });
              return;
            }
            if (await handleCartDestinationSyncError(pollErr, customer, cart)) {
              return;
            }
            if (availabilityCtrl && availabilityCtrl.handleSyncError(pollErr, cart)) {
              dismissSyncModal(true);
              return;
            }
            reportSyncOutcome(formatSyncErrorMessage(pollErr, cfg), 'error', { canRepost: true });
            return;
          }
          completedResult = /** @type {Record<string, unknown>} */ (pollOutcome.data || {});
        } else if (!result.res.ok) {
          var errData = /** @type {Record<string, unknown>} */ (result.data || {});
          if (await handleCartDestinationSyncError(errData, customer, cart)) {
            return;
          }
          if (availabilityCtrl && availabilityCtrl.handleSyncError(errData, cart)) {
            dismissSyncModal(true);
            return;
          }
          var errMsg = formatSyncErrorMessage(errData, cfg);
          reportSyncOutcome(errMsg, 'error', { canRepost: true });
          return;
        } else {
          completedResult = unwrapApiData(result.data);
        }

        if (availabilityCtrl && typeof availabilityCtrl.reset === 'function') {
          availabilityCtrl.reset();
        }

        var successMsg = formatSyncSuccessMessage(cfg, completedResult);
        reportSyncOutcome(successMsg, 'success');
        await clearShopifyCartAfterSync();
      } catch {
        reportSyncOutcome(String(cfg.syncErrorFallback || 'Unable to sync order'), 'error', {
          unresolved: true,
          syncId: acceptedSyncId,
        });
      } finally {
        if (activeCartSyncPollAbort === pollAbortController) {
          activeCartSyncPollAbort = null;
        }
      }
    }

    syncBtn.addEventListener('click', async function () {
      if (syncInProgress) return;
      var existingModal = syncModalElement();
      if (existingModal && !existingModal.hasAttribute('hidden') && existingModal.getAttribute('data-state') !== 'loading') {
        dismissSyncModal(true);
      }
      syncInProgress = true;
      var labelEl = qs('[data-sync-button-label]', root);
      var defaultLabel = cfg.syncButtonTextDefault != null ? String(cfg.syncButtonTextDefault) : 'SEND FOR REVIEW';

      try {
        if (!isSalesRepOk(cfg)) {
          showToast(cfg, String(cfg.loginWarningFallback || 'Please log in as a sales representative to sync this order.'), 'error');
          return;
        }

        var cartResult = await fetchJson(String(cfg.cartJsUrl || '/cart.js'));
        var cart = /** @type {Record<string, unknown>} */ (cartResult.data);
        if (!cartResult.res.ok) {
          showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
          return;
        }

        var validationError = validateCartLinesForSync(cart);
        if (validationError) {
          showToast(cfg, validationError, 'error');
          return;
        }

        var pricingError = validateCartPricing(cart, cfg, root);
        if (pricingError) {
          showToast(cfg, pricingError, 'error');
          return;
        }

        var Picker = typeof window !== 'undefined' ? window.AonegndCustomerPicker : undefined;
        if (!Picker || typeof Picker.open !== 'function') {
          showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
          return;
        }

        var selected = await Picker.open({
          apiBaseUrl: cfg.apiBaseUrl,
          customersPath: cfg.customersPath,
          salesRepEmail: getSalesRepEmail(cfg),
          title: cfg.customerPickerTitle,
          doneLabel: cfg.customerPickerDoneLabel,
          cancelLabel: cfg.customerPickerCancelLabel,
          searchPlaceholder: cfg.customerPickerSearchPlaceholder,
        });

        if (!selected || parsePositiveIntegerId(selected.id) == null) {
          if (selected) {
            showToast(cfg, 'Please select a valid customer before syncing this order.', 'error');
          }
          return;
        }

        var destination = await openCartDestinationPicker(selected);
        if (!destination) return;

        syncBtn.disabled = true;
        syncBtn.classList.add('is-loading');
        if (labelEl) labelEl.textContent = String(cfg.syncLoadingText || 'SYNCING...');

        await performSync(cart, selected, destination);
      } finally {
        syncInProgress = false;
        syncBtn.disabled = false;
        syncBtn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = defaultLabel;
        updateSalesRepGate(root, cfg);
      }
    });
  }

  function boot(scope) {
    var rootEl = scope instanceof HTMLElement ? scope : document.documentElement;
    var candidates = rootEl.querySelectorAll('[data-b2b-cart-review]');
    for (var i = 0; i < candidates.length; i++) {
      var root = candidates[i];
      if (!(root instanceof HTMLElement)) continue;
      if (root.dataset.b2bCartReviewInit === '1') continue;
      var section = root.closest('.shopify-section');
      var cfgEl =
        section && section instanceof HTMLElement ? qs('[data-b2b-cart-review-config]', section) : null;
      if (!(cfgEl instanceof HTMLElement)) cfgEl = qs('[data-b2b-cart-review-config]');
      if (!(cfgEl instanceof HTMLElement)) continue;
      /** @type {Record<string, unknown>} */
      var cfg;
      try {
        cfg = JSON.parse(cfgEl.textContent || '{}');
      } catch {
        continue;
      }
      root.dataset.b2bCartReviewInit = '1';
      init(root, cfg);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document.documentElement);
    });
  } else {
    boot(document.documentElement);
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target instanceof HTMLElement) boot(ev.target);
  });
})();
